/* eslint-disable no-console */
// Import axios ESM build to avoid bundlers resolving the `.cjs` file as a static asset.
// Import only the types for TypeScript correctness.
import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';
import { getTokenIfValid, clearSession, getLoginPath } from './session';
import { 
  User, 
  Personal, 
  LoginForm, 
  RegisterForm,
  ApiResponse,
  PaginatedResponse,
  DashboardStats,
  PersonalDisponible,
  CreatePersonalDisponibleData,
  ExtendedRegisterForm,
  Cliente,
  Nodo,
  Cartera
} from '../types';

import { API_CONFIG, FILE_CONFIG } from '../config/api';
import { mapTipoDocumentoToFolder } from '../utils/documentFolders';
import type { EstadoIngreso } from '../utils/roles';
import { log as cloudRunLog, timeStart as cloudRunTimeStart, timeEnd as cloudRunTimeEnd, summarizeResponse as cloudRunSummarize } from '../utils/cloudRunLogger';

// Note: tests that mock axios can still mock the module name 'axios'.

// Área responsable de un requisito/prerrequisito (overlay backend
// zc_temp.prerrequisito_responsable), tal como la define
// "Documentos para acreditación.xlsx" (columna "Corresponde a" de cada mandante).
// Viene anexada en los prerrequisitos que devuelven GET /prerrequisitos/cliente/:id
// y GET /prerrequisitos/clientes/:id/faltantes/:rut. `null` = requisito sin área asignada.
export type PrereqArea = 'RRHH' | 'Prevención' | 'N/A';

// Plan de POST /personal-roles/clientes/:id/estado-masivo (backend services/estadoIngresoService.js).
export interface FilaPlanEstadoIngreso {
  rut: string;
  nombre: string | null;
  entrada?: string;
  estado_nuevo?: EstadoIngreso;
  estado_anterior?: EstadoIngreso | null;
  fuera_empresa?: boolean;
  rol?: string;
  se_dara_de_baja?: boolean;
}
export interface PlanEstadoIngreso {
  agregar: FilaPlanEstadoIngreso[];
  actualizar: FilaPlanEstadoIngreso[];
  sin_cambio: FilaPlanEstadoIngreso[];
  ausentes: FilaPlanEstadoIngreso[];
  no_encontrados: { entrada: string; estado: EstadoIngreso }[];
  ambiguos: { entrada: string; estado: EstadoIngreso; candidatos: { rut: string; nombre: string }[] }[];
  invalidos: unknown[];
  lineas_invalidas: { linea: number; texto: string }[];
}
export interface HistorialEstadoIngreso {
  id: number;
  rut: string;
  nombre: string | null;
  estado_anterior: EstadoIngreso | null;
  estado_nuevo: EstadoIngreso | null;
  motivo: string | null;
  origen: 'manual' | 'planilla' | 'agregar';
  usuario: string | null;
  creado_en: string;
}

// Último recálculo completo de la cobertura (zc_temp.cobertura_recalculo en el backend).
// `usuario_nombre` es null si la fecha salió de la proyección y no de un recálculo registrado.
export interface CoberturaMatrizMeta {
  calculado_en: string | null;
  calculado_por: number | null;
  usuario_nombre: string | null;
  duracion_ms?: number;
}

// Rol editable desde /configuracion/roles. `permisos` mezcla acceso (app.*), pestañas
// (tab.*) y acciones. `sembrado: false` = el rol todavía resuelve por el respaldo del
// código (no tiene filas en BD); `sistema: true` = no se puede editar (Administrador).
export interface RolConfigurable {
  key: string;
  nombre: string;
  descripcion: string;
  activo: boolean;
  sistema: boolean;
  sembrado: boolean;
  permisos: string[];
  usuarios: number;
}

export interface CatalogoItem {
  key: string;
  label: string;
  descripcion?: string;
  ruta?: string;
  requiere?: string[];
}

export interface RolesCatalogo {
  acceso: CatalogoItem[];
  pestanas: { app: string; grupos: { grupo: string; items: CatalogoItem[] }[] }[];
  acciones: { grupo: string; items: CatalogoItem[] }[];
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Some environments may provide an axios-like object without `create`.
    // Prefer `axios.create(...)` when available, otherwise use the imported axios directly
    // and ensure it has `defaults` and `interceptors` shape expected by the rest of the class.
    const axiosLib = axios;
    // Defensive check: axios should be an object or function with a `create` method.
    if (!axiosLib || (typeof axiosLib !== 'object' && typeof axiosLib !== 'function')) {
      // Common cause: build resolved axios to a static asset (string path) or it's missing.
      // Throw a descriptive error to help debugging instead of attempting to mutate a primitive.
      // eslint-disable-next-line no-console
      console.error('ApiService - invalid axios import:', axiosLib);
      throw new Error([
        'Invalid `axios` import detected. Expected axios module (object/function),',
        `but got a value of type '${typeof axiosLib}'. This often happens when webpack resolves 'axios' to a static asset or when axios is not installed.`,
        'Remedies: (1) ensure `axios` is installed (`npm install axios`),',
        "(2) check your project for files named 'axios.*' that could shadow the package import,", 
        "(3) check webpack aliases or module resolution that may map 'axios' to an asset.",
      ].join(' '));
    }

    if (axiosLib && typeof axiosLib.create === 'function') {
      this.api = axiosLib.create({
        baseURL: API_CONFIG.BASE_URL,
        timeout: API_CONFIG.TIMEOUT,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        withCredentials: false,
      });
    } else if (axiosLib) {
      // Use axiosLib directly as a fallback. Attach minimal defaults/interceptors if missing.
      this.api = axiosLib;
      if (typeof this.api !== 'object' && typeof this.api !== 'function') {
        // Shouldn't happen due to the guard above, but keep defensive fallback
        throw new Error('Axios import is present but is not usable as HTTP client. See console for details.');
      }
      if (!this.api.defaults) {
        this.api.defaults = { baseURL: API_CONFIG.BASE_URL, withCredentials: false } as any;
      }
      if (!this.api.interceptors) {
        this.api.interceptors = { request: { use: () => {} }, response: { use: () => {} } } as any;
      }
    } else {
      throw new Error('Axios is not available in this environment');
    }

    // Mostrar baseURL en consola al iniciar el cliente API para diagnóstico
    // (ayuda a verificar que el frontend está llamando al backend correcto)
    try {
      const baseURL = (this.api.defaults && this.api.defaults.baseURL) || API_CONFIG.BASE_URL;
      const isProduction = baseURL.includes('zc-backend') || baseURL.includes('southamerica-west1.run.app');
      console.log(`🌍 [API] Conectado a ${isProduction ? 'PRODUCCIÓN' : 'DESARROLLO'}:`, baseURL, 'withCredentials:', (this.api.defaults && this.api.defaults.withCredentials));
      cloudRunLog('API client initialized', { baseURL, withCredentials: (this.api.defaults && this.api.defaults.withCredentials) });
    } catch (e) {
      // ignore logging errors
    }

    // Interceptor para agregar token a las peticiones
    this.api.interceptors.request.use(
      (config) => {
        // Permitir llamadas públicas como login/refresh/register/logout sin requerir token
        const url = String(config.url || '');
        const publicPaths = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/register'];
        if (publicPaths.some((p) => url.includes(p))) {
          // Si hay token válido, adjuntarlo; si no, permitir la petición (ej. /auth/login)
          const tokenPublic = getTokenIfValid();
          if (tokenPublic) config.headers.Authorization = `Bearer ${tokenPublic}`;
          return config;
        }

        const token = getTokenIfValid();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        }

        // Si no hay token válido para rutas protegidas: limpiar sesión y forzar login
        // (la ruta de login depende del scope activo: '/login' o '/pre-sisi/login')
        clearSession();
        try {
          window.location.href = getLoginPath();
        } catch (e) {}
        return Promise.reject(new Error('Session expired'));
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar respuestas y errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Manejar errores 401 (no autorizado): sesión vencida -> volver al login. Salvo que
        // el 401 venga del PROPIO login (contraseña incorrecta): ahí recargar la página
        // borraba el mensaje "Email o contraseña incorrectos" y parecía que el login solo
        // "se reiniciaba", sin decir por qué.
        const esLogin = String(error.config?.url || '').includes('/auth/login');
        if (error.response?.status === 401 && !esLogin) {
          clearSession();
          window.location.href = getLoginPath();
        }
        
        // Filtrar errores 404 de imágenes de perfil para evitar spam en consola
        if (error.response?.status === 404 && 
            ((error.config?.url?.includes('/personal/') && error.config?.url?.includes('/image')) ||
             (error.config?.url?.includes('/profile-photos') && error.config?.url?.includes('/image')))) {
          // No loguear estos errores ya que son esperados cuando no hay imagen
          // Crear un error silencioso que no se muestre en consola
          const silentError = new Error('No image found');
          silentError.name = 'SilentError';
          return Promise.reject(silentError);
        }
        
        // Filtrar errores 404 de endpoints de programación semanal (tienen fallback)
        if ((error.response?.status === 404 || error.response?.status === 400) && 
            error.config?.url?.includes('/programacion/semana')) {
          // Estos endpoints tienen fallback en el código, no mostrar error en consola
          const silentError = new Error('Endpoint not available or bad request, using fallback');
          silentError.name = 'SilentError';
          (silentError as any).response = error.response;
          (silentError as any).config = error.config;
          return Promise.reject(silentError);
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Helper: try new '/requisitos' path first, fallback to legacy '/prerrequisitos' on 404
  private async getWithLegacy(newPath: string, oldPath: string, config?: any) {
    try {
      const resp: AxiosResponse<any> = await this.api.get(newPath, config);
      return resp;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // Try legacy
        const resp2: AxiosResponse<any> = await this.api.get(oldPath, config);
        return resp2;
      }
      throw err;
    }
  }

  private async postWithLegacy(newPath: string, oldPath: string, data?: any, config?: any) {
    try {
      const resp: AxiosResponse<any> = await this.api.post(newPath, data, config);
      return resp;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        const resp2: AxiosResponse<any> = await this.api.post(oldPath, data, config);
        return resp2;
      }
      throw err;
    }
  }

  private async putWithLegacy(newPath: string, oldPath: string, data?: any, config?: any) {
    try {
      const resp: AxiosResponse<any> = await this.api.put(newPath, data, config);
      return resp;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        const resp2: AxiosResponse<any> = await this.api.put(oldPath, data, config);
        return resp2;
      }
      throw err;
    }
  }

  private async deleteWithLegacy(newPath: string, oldPath: string, config?: any) {
    try {
      const resp: AxiosResponse<any> = await this.api.delete(newPath, config);
      return resp;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        const resp2: AxiosResponse<any> = await this.api.delete(oldPath, config);
        return resp2;
      }
      throw err;
    }
  }

  // Métodos de autenticación
  // `app` le dice al backend desde qué pantalla se inicia sesión: responde 403 si el rol
  // no tiene acceso a esa aplicación (app.sisi / app.presisi, configurable por rol).
  async login(credentials: LoginForm, app?: 'sisi' | 'presisi'): Promise<{ message: string; user: User; token: string }> {
    const response: AxiosResponse<{ message: string; user: User; token: string }> = await this.api.post('/auth/login', app ? { ...credentials, app } : credentials);
    return response.data;
  }

  async register(_userData: RegisterForm): Promise<{ message: string; user: User; token: string }> {
    // Registro deshabilitado desde frontend - evitar llamadas accidentales al endpoint
    // El backend debe encargarse de bloquear registro público, pero para seguridad
    // hacemos que el cliente rechace la operación aquí con un mensaje claro.
    throw new Error('Registro deshabilitado. Contacta al administrador.');
  }

  async getCurrentUser(): Promise<ApiResponse<{
    id: number;
    rut: string;
    nombres: string;
    apellidos: string;
    email: string;
    cargo: string;
    rol?: string;
    perfil?: string;
    permissions?: string[];
    cartera_id: number;
    cartera_nombre: string;
    activo: boolean;
    fecha_creacion: string;
    ultimo_acceso: string;
  }>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auth/me');
    return response.data;
  }

  async getAuthPermissions(): Promise<ApiResponse<{ rol: string; perfil?: string; permissions: string[] }>> {
    const response: AxiosResponse<ApiResponse<{ rol: string; perfil?: string; permissions: string[] }>> = await this.api.get('/auth/permissions');
    return response.data;
  }

  async registerWithPersonalDisponible(_userData: ExtendedRegisterForm): Promise<ApiResponse<{ token: string; user: User; personal: PersonalDisponible }>> {
    // Registro deshabilitado: evitar crear usuarios desde frontend.
    // Dejamos una excepción clara para que cualquier llamada falle rápido.
    throw new Error('Registro deshabilitado. Contacta al administrador.');
  }

  async logout(): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.post('/auth/logout');
    return response.data;
  }


  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    const response: AxiosResponse<ApiResponse<{ token: string }>> = await this.api.post('/auth/refresh');
    return response.data;
  }

  // Métodos de Personal
  async getPersonal(page = 1, limit = 10, search = '', _filters = ''): Promise<PaginatedResponse<Personal>> {
    const offset = (page - 1) * limit;

    // `fields` (CSV) recorta las columnas que devuelve el backend. Quien solo necesita
    // resolver nombres no tiene por qué descargar correo, teléfono, dirección y tallas:
    // con fields la misma página pasa de 115 KB a 23 KB.
    let camposParam = '';
    try {
      const f = _filters ? JSON.parse(_filters) : null;
      if (f && typeof f.fields === 'string' && f.fields.trim()) {
        camposParam = `&fields=${encodeURIComponent(f.fields.trim())}`;
      }
    } catch {
      // _filters no siempre es JSON; sin fields se pide todo, como antes.
    }
    
    // SIEMPRE obtener todos los registros para aplicar búsqueda y filtros en cliente
    // El backend limita a 100 registros por petición, así que hacemos múltiples peticiones
    console.log('🔍 [API.getPersonal] Obteniendo todos los registros. Búsqueda:', search || '(sin búsqueda)');
    try {
      // Primera petición: trae el total Y la página 0 (se reutiliza, no se re-pide en el loop)
      const batchSize = 100;
      const firstResponse = await this.api.get(`/personal-disponible?limit=${batchSize}&offset=0${camposParam}`);
      const totalRecords = firstResponse.data?.pagination?.total || 0;
      console.log('📊 [API] Total de registros en backend:', totalRecords);

      let allPersonal: any[] = (firstResponse.data?.success && Array.isArray(firstResponse.data?.data))
        ? firstResponse.data.data.slice()
        : [];
      const totalPages = Math.ceil(totalRecords / batchSize);

      console.log(`🔄 [API] Obteniendo ${totalPages} página(s) de ${batchSize} registros cada una (en paralelo)...`);

      // El resto de páginas (1..totalPages-1) se piden todas en paralelo en vez de en serie.
      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
        const responses = await Promise.all(
          remainingPages.map(pageNum => this.api.get(`/personal-disponible?limit=${batchSize}&offset=${pageNum * batchSize}${camposParam}`))
        );
        for (const response of responses) {
          if (response.data.success && response.data.data) {
            allPersonal = allPersonal.concat(response.data.data);
          }
        }
      }

      console.log('✅ [API] Todos los registros obtenidos:', allPersonal.length);
      console.log('🔍 [API] Primeros 5 RUTs:', allPersonal.slice(0, 5).map((p: any) => p.rut));
      
      const benjamin = allPersonal.filter((p: any) => 
        (p.nombres || p.nombre || '').toLowerCase().includes('benjamin') ||
        (p.apellido || '').toLowerCase().includes('benjamin') ||
        (p.rut || '').includes('21364997')
      );
      console.log('🔍 [API] Benjamin registros encontrados:', benjamin.length, benjamin.map((p: any) => ({ rut: p.rut, nombres: p.nombres, nombre: p.nombre, apellido: p.apellido })));
        
      // Si hay búsqueda activa, aplicar filtros
      let filteredPersonal = allPersonal;
      if (search && search.trim()) {
        const searchTerm = search.trim().toLowerCase();
          
        // Filtrar en el frontend por múltiples campos (mejor cobertura de nombres y RUTs)
        const removeDiacritics = (s: string) => {
          if (!s) return '';
          try {
            return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
          } catch (e) {
            return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
          }
        };

        const normalizedSearch = removeDiacritics(searchTerm.trim().toLowerCase());

        filteredPersonal = allPersonal.filter((persona: any) => {
          const rawRut = (persona.rut || persona.id || '').toString();
          const rutNormalized = rawRut.replace(/[.-]/g, '').toLowerCase();
          const rutPlain = rawRut.toLowerCase();

          const nombresFieldRaw = (persona.display_name || persona.nombres || persona.nombre || persona.nombre_completo || '').toString();
          const apellidoFieldRaw = (persona.apellido || '').toString();
          const cargoRaw = (persona.cargo || '').toString();
          const zonaRaw = (persona.zona_geografica || persona.zona || '').toString();

          const nombresField = removeDiacritics(nombresFieldRaw);
          const apellidoField = removeDiacritics(apellidoFieldRaw);
          const fullName = `${nombresField} ${apellidoField}`.trim();
          const cargo = removeDiacritics(cargoRaw);
          const zona = removeDiacritics(zonaRaw);
          // Token-based matching: all tokens in the search must appear in the candidate
          const makeTokens = (s: string) => (s || '').toString().split(/\s+/).filter(Boolean);
          const searchTokens = makeTokens(normalizedSearch);

          const candidateText = `${fullName} ${cargo} ${zona}`.trim();
          const candidateTokens = makeTokens(candidateText);

          const allSearchTokensMatch = searchTokens.every((t) =>
            candidateTokens.some((ct) => ct.includes(t) || ct.startsWith(t) || ct.endsWith(t))
          );

          // Also allow direct rut matches (formatted or plain)
          const rutMatch = rutPlain.includes(searchTerm) || rutNormalized.includes(normalizedSearch.replace(/[.-]/g, ''));

          return rutMatch || allSearchTokensMatch;
        });
          
        console.log('🔍 Personal filtrado (total):', filteredPersonal.length);
        console.log('🔍 Término de búsqueda:', searchTerm);
        console.log('🔍 Resultados encontrados:', filteredPersonal.map((p: any) => ({ rut: p.rut, nombres: p.display_name || p.nombres })));
      }
        
      // Aplicar paginación manual a los resultados filtrados
      const startIndex = offset;
      const endIndex = startIndex + limit;
      const paginatedData = filteredPersonal.slice(startIndex, endIndex);
        
      console.log('🔍 Datos paginados:', {
        total: filteredPersonal.length,
        startIndex,
        endIndex,
        paginatedCount: paginatedData.length
      });
        
      return {
        success: true,
        data: paginatedData,
        pagination: {
          total: filteredPersonal.length,
          limit: limit,
          offset: offset
        }
      };
    } catch (error) {
      console.warn('⚠️ Error en búsqueda global:', error);
      throw error;
    }
  }

  // Obtener personal con información de centro de costo
  async getPersonalWithCentro(_page = 1, _limit = 50, search = '', filters: any = {}): Promise<PaginatedResponse<any>> {
    try {
      // Hacer una primera petición para conocer el total y luego descargar en batches (igual que getPersonal)
      const initialLimit = 100;
      const initialOffset = 0;
      const paramsFirst: Record<string, any> = { limit: initialLimit, offset: initialOffset };
      if (search) paramsFirst.search = search;
      if (filters?.id_centro_costo) paramsFirst.id_centro_costo = filters.id_centro_costo;

      const qsFirst = Object.keys(paramsFirst).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(paramsFirst[k])}`).join('&');
      const firstResponse: AxiosResponse<any> = await this.api.get(`/personal-disponible/with-centro?${qsFirst}`);
      const payloadFirst = firstResponse.data || {};
      const totalRecords = payloadFirst.pagination?.count ?? (Array.isArray(payloadFirst.data) ? payloadFirst.data.length : 0);

      let allItems: any[] = Array.isArray(payloadFirst.data) ? payloadFirst.data.slice() : [];

      if (totalRecords > allItems.length) {
        const batchSize = initialLimit;
        const totalPages = Math.ceil(totalRecords / batchSize);
        for (let p = 1; p < totalPages; p++) {
          const offset = p * batchSize;
          const params: Record<string, any> = { limit: batchSize, offset };
          if (search) params.search = search;
          if (filters?.id_centro_costo) params.id_centro_costo = filters.id_centro_costo;
          const qs = Object.keys(params).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
          const resp: AxiosResponse<any> = await this.api.get(`/personal-disponible/with-centro?${qs}`);
          const pageItems = Array.isArray(resp.data?.data) ? resp.data.data : [];
          allItems = allItems.concat(pageItems);
        }
      }

      // Debug: log counts to help diagnose truncated results
      try {
        console.log('🔍 [getPersonalWithCentro] totalRecords:', totalRecords, 'allItems.length:', allItems.length);
        console.log('🔍 [getPersonalWithCentro] sample RUTs:', allItems.slice(0, 10).map((p: any) => p.rut || p.id));
      } catch (e) {}

      return {
        success: !!payloadFirst.success,
        data: allItems,
        pagination: {
          total: totalRecords,
          limit: initialLimit,
          offset: 0
        }
      } as any;
    } catch (error) {
      console.error('❌ getPersonalWithCentro error:', error);
      throw error;
    }
  }

  async getCentrosCosto(): Promise<any[]> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/centros-costo');
      const payload = response.data || {};
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (error) {
      console.error('❌ getCentrosCosto error:', error);
      return [];
    }
  }

  // ── Centros de costo: CRUD con jerarquía ──────────────────────────────────
  // GET /centros-costo?tree=true → árbol anidado (children[])
  async getCentrosCostoTree(): Promise<any[]> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/centros-costo', { params: { tree: true } });
      const payload = response.data || {};
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (error) {
      console.error('❌ getCentrosCostoTree error:', error);
      return [];
    }
  }

  // POST /centros-costo
  async createCentroCosto(body: {
    id_centro_costo: string; nombre: string; sede?: string; activo?: boolean; parent_id?: string | null;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/centros-costo', body);
    return response.data;
  }

  // PUT /centros-costo/:id
  async updateCentroCosto(id: string, body: {
    nombre?: string; sede?: string; activo?: boolean; parent_id?: string | null;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/centros-costo/${encodeURIComponent(id)}`, body);
    return response.data;
  }

  // DELETE /centros-costo/:id (?force=true si tiene hijos)
  async deleteCentroCosto(id: string, force = false): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/centros-costo/${encodeURIComponent(id)}`, {
      params: force ? { force: true } : undefined,
    });
    return response.data;
  }

  // Personal disponible creado recientemente (ya viene ordenado por created_at desc)
  async getPersonalReciente(params: { dias?: number; limit?: number; estado_id?: number } = {}): Promise<{ success: boolean; data: any[]; meta?: any }> {
    const response: AxiosResponse<any> = await this.api.get('/personal-disponible/recientes', { params });
    return response.data;
  }

  // Personal marcado como "Nuevo" por el backend (fuente de verdad: campo es_nuevo).
  // Se pide con ?es_nuevo=true y, como red de seguridad, se vuelve a filtrar por el
  // campo es_nuevo===true en el cliente (por si el backend aún no honra el query param
  // antes del deploy). NO se recalcula la antigüedad por fechas en el front.
  async getPersonalNuevos(): Promise<any[]> {
    const batchSize = 100;
    let all: any[] = [];
    try {
      const first: AxiosResponse<any> = await this.api.get('/personal-disponible', {
        params: { es_nuevo: true, limit: batchSize, offset: 0 },
      });
      const total = first.data?.pagination?.total ?? (Array.isArray(first.data?.data) ? first.data.data.length : 0);
      if (Array.isArray(first.data?.data)) all = all.concat(first.data.data);

      const totalPages = Math.ceil(total / batchSize);
      for (let pageNum = 1; pageNum < totalPages; pageNum++) {
        const resp: AxiosResponse<any> = await this.api.get('/personal-disponible', {
          params: { es_nuevo: true, limit: batchSize, offset: pageNum * batchSize },
        });
        if (Array.isArray(resp.data?.data)) all = all.concat(resp.data.data);
      }
    } catch (error) {
      console.error('❌ getPersonalNuevos falló:', error);
      return [];
    }
    // Confiar en el campo es_nuevo del backend (no recalcular 30 días en el front)
    return all.filter((p) => p?.es_nuevo === true);
  }

  async getPersonalById(id: string): Promise<ApiResponse<Personal>> {
    const response: AxiosResponse<ApiResponse<Personal>> = await this.api.get(`/personal-disponible/${id}`);
    // Debug: verificar qué devuelve el backend
    console.log('🔍 getPersonalById - respuesta del backend:', response.data);
    console.log('🔍 getPersonalById - campos nombre/apellido/nombres:', {
      nombre: response.data?.data?.nombre,
      apellido: response.data?.data?.apellido,
      nombres: (response.data?.data as any)?.nombres
    });
    return response.data;
  }


  async updatePersonal(id: string, personalData: Partial<Personal>): Promise<ApiResponse<Personal>> {
    // eslint-disable-next-line no-console
    console.log('🌐 Actualizando personal en endpoint principal:', {
      id,
      personalData,
      url: `/personal-disponible/${id}`
    });
    // Use GET + PUT merge to ensure partial updates don't erase fields
    try {
      console.log('🔁 Realizando GET para merge antes de PUT...');
      const getResp: AxiosResponse<any> = await this.api.get(`/personal-disponible/${id}`);
      const existing = getResp.data?.data ?? getResp.data ?? getResp;

      const merged: any = {
        sexo: personalData.sexo ?? existing.sexo,
        fecha_nacimiento: personalData.fecha_nacimiento ?? existing.fecha_nacimiento,
        licencia_conducir: personalData.licencia_conducir ?? existing.licencia_conducir,
        cargo: personalData.cargo ?? existing.cargo,
        estado_id: personalData.estado_id ?? existing.estado_id,
        nombres: (personalData as any).nombres ?? existing.nombres ?? `${existing.nombre || ''} ${existing.apellido || ''}`.trim(),
        correo_electronico: (personalData as any).email ?? existing.correo_electronico ?? existing.email ?? existing.correo,
        telefono: personalData.telefono ?? existing.telefono ?? existing.telefono_2,
        region: (personalData as any).ubicacion?.region ?? existing.region ?? existing.ubicacion?.region,
        ciudad: (personalData as any).ubicacion?.ciudad ?? existing.ciudad ?? existing.ubicacion?.ciudad,
        comuna: (personalData as any).ubicacion?.comuna ?? existing.comuna ?? existing.ubicacion?.comuna,
        direccion: (personalData as any).ubicacion?.direccion ?? existing.direccion ?? existing.ubicacion?.direccion,
        ...existing,
        ...personalData,
      };

      const putResp: AxiosResponse<ApiResponse<Personal>> = await this.api.put(`/personal-disponible/${id}`, merged);
      console.log('✅ PUT merge exitoso:', putResp.data);
      return putResp.data;
    } catch (err) {
      console.error('❌ Error actualizando personal con PUT merge:', err);
      throw err;
    }
  }

  async createPersonalDisponible(personalData: CreatePersonalDisponibleData): Promise<ApiResponse<PersonalDisponible>> {
    // eslint-disable-next-line no-console
    console.log('🌐 Creando personal disponible con datos:', JSON.stringify(personalData, null, 2));
    try {
      const response: AxiosResponse<ApiResponse<PersonalDisponible>> = await this.api.post('/personal-disponible', personalData);
      // eslint-disable-next-line no-console
      console.log('✅ Personal creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error al crear personal:', error);
      // eslint-disable-next-line no-console
      console.error('❌ Datos enviados:', JSON.stringify(personalData, null, 2));
      throw error;
    }
  }

  async deletePersonal(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/personal-disponible/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA NOMBRES ====================
  
  // Crear nombre en la tabla nombres
  async createNombre(nombreData: any): Promise<ApiResponse<any>> {
    console.log('🌐 Creando nombre en tabla nombres:', nombreData);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/nombres', nombreData);
      console.log('✅ Nombre creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al crear nombre:', error);
      throw error;
    }
  }

  // Actualizar nombre en la tabla nombres (endpoint específico que sincroniza con Google Drive)
  async updateNombre(rut: string, nombreData: any): Promise<ApiResponse<any>> {
    console.log('🌐 [API] Actualizando nombre con endpoint /api/nombres/:rut', { rut, nombreData });
    try {
      // El endpoint /api/nombres/:rut espera: { nombre, apellido, sexo?, fecha_nacimiento?, licencia_conducir? }
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/nombres/${rut}`, nombreData);
      console.log('✅ [API] Nombre actualizado exitosamente:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] Error al actualizar nombre:', error);
      console.error('❌ [API] Detalles del error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  // Obtener nombre por RUT
  async getNombreByRut(rut: string): Promise<ApiResponse<any>> {
    console.log('🌐 Obteniendo nombre para RUT:', rut);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/nombres/${rut}`);
      console.log('✅ Nombre obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener nombre:', error);
      throw error;
    }
  }

  // Cambiar RUT (operación crítica con transacción)
  async changeRut(oldRut: string, newRut: string): Promise<ApiResponse<any>> {
    console.log('🌐 [API] Cambiando RUT:', { oldRut, newRut });
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
        `/personal-disponible/${oldRut}/change-rut`,
        { nuevoRut: newRut }
      );
      console.log('✅ [API] RUT cambiado exitosamente:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] Error al cambiar RUT:', error);
      console.error('❌ [API] Detalles del error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  // ==================== MÉTODOS PARA PERSONAL DISPONIBLE (CORREGIDOS) ====================
  
  // Obtener personal por RUT (usando el endpoint correcto)
  async getPersonalByRut(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/personal-disponible/${rut}`);
    return response.data;
  }

  // Buscar personal (usando el endpoint correcto con filtros)
  async searchPersonal(searchTerm: string): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/personal-disponible?search=${encodeURIComponent(searchTerm)}`);
    return response.data;
  }

  // Obtener estadísticas de personal (usando el endpoint correcto)
  async getPersonalStats(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/personal-disponible/stats/cargos');
    return response.data;
  }

  // Crear personal (usando el endpoint correcto)
  async createPersonal(nombreData: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/personal-disponible', nombreData);
    return response.data;
  }

  // Actualizar personal (usando el endpoint correcto)
  async updatePersonalData(rut: string, nombreData: any): Promise<ApiResponse<any>> {
    try {
      // Always ensure a full merged PUT to avoid losing fields
      const getResp: AxiosResponse<any> = await this.api.get(`/personal-disponible/${rut}`);
      const existing = getResp.data?.data ?? getResp.data ?? getResp;
      const merged = { ...existing, ...nombreData };
      const putResp: AxiosResponse<ApiResponse<any>> = await this.api.put(`/personal-disponible/${rut}`, merged);
      return putResp.data;
    } catch (err) {
      console.error('❌ updatePersonalData PUT merge failed:', err);
      throw err;
    }
  }

  // Eliminar personal (usando el endpoint correcto)
  async deletePersonalData(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/personal-disponible/${rut}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA ESTADOS ====================
  
  // Obtener todos los estados (con soporte para paginación y búsqueda)
  async getEstados(params?: { limit?: number; offset?: number; search?: string }): Promise<ApiResponse<any[]>> {
    console.log('🌐 Haciendo petición a /estados con params:', params);
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/estados', { params });
    console.log('✅ Respuesta de /estados:', response.data);
    return response.data;
  }

  // ==================== MÉTODOS PARA CURSOS/CERTIFICACIONES ====================
  
  // Obtener todos los cursos con filtros
  // `sin_documento` omite los cursos cuyo certificado YA viene como documento en
  // GET /documentos?todos=true (vinculados por el overlay zc_temp.curso_documento). Lo usa
  // Estado de Documentación, que concatena ambas listas y deduplica por id: como los cursos
  // llevan id `curso-#` y los documentos id numérico, el par nunca colapsaba solo y cada
  // certificado se contaba dos veces. Es opt-in: el catálogo de Cursos y el dashboard piden
  // la lista completa y no cambian de comportamiento.
  async getCursos(filters?: { rut?: string; curso?: string; limit?: number; offset?: number; sin_documento?: boolean }): Promise<ApiResponse<any[]>> {
    // Si el caller especifica limit/offset, respetar y hacer una sola llamada
    const baseParams = new URLSearchParams();
    if (filters?.rut) baseParams.append('rut', filters.rut);
    if (filters?.curso) baseParams.append('curso', filters.curso);
    if (filters?.sin_documento) baseParams.append('sin_documento', 'true');

    if (typeof filters?.limit === 'number' || typeof filters?.offset === 'number') {
      if (typeof filters?.limit === 'number') baseParams.append('limit', String(filters.limit));
      if (typeof filters?.offset === 'number') baseParams.append('offset', String(filters.offset));
      const resp: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/cursos${baseParams.toString() ? '?' + baseParams.toString() : ''}`);
      return resp.data;
    }

    // Obtener todos los cursos en lotes (como en getDocumentos)
    try {
      const batchSize = 200;
      const firstParams = new URLSearchParams(baseParams.toString());
      firstParams.append('limit', String(batchSize));
      firstParams.append('offset', '0');
      
      console.log('📦 getCursos - Iniciando paginación, primera petición...');
      const firstResp: AxiosResponse<ApiResponse<any>> = await this.api.get(`/cursos?${firstParams.toString()}`);

      // Determinar total de registros
      const firstData = firstResp.data as any;
      const firstList: any[] = Array.isArray(firstData?.data) ? firstData.data : (Array.isArray(firstData) ? firstData : []);
      const totalRecords: number = (firstData?.pagination?.total) ?? firstList.length;

      console.log('📦 getCursos - Primera página:', {
        recibidos: firstList.length,
        totalEnBackend: totalRecords,
        tienePaginacion: !!firstData?.pagination
      });

      // Si no hay paginación o ya tenemos todo, devolver lo que hay
      if (!firstData?.pagination || totalRecords <= firstList.length) {
        console.log('📦 getCursos - Sin paginación o ya tenemos todo. Total:', firstList.length);
        return {
          success: true,
          data: firstList,
          pagination: {
            total: firstList.length,
            limit: firstList.length,
            offset: 0,
          }
        } as any;
      }

      // Acumular todas las páginas
      let allCursos = [...firstList];
      const totalPages = Math.ceil(totalRecords / batchSize);
      console.log('📦 getCursos - Páginas totales a cargar:', totalPages);
      
      for (let pageNum = 1; pageNum < totalPages; pageNum++) {
        const offset = pageNum * batchSize;
        const pageParams = new URLSearchParams(baseParams.toString());
        pageParams.append('limit', String(batchSize));
        pageParams.append('offset', String(offset));
        
        console.log(`📦 getCursos - Cargando página ${pageNum + 1}/${totalPages} (offset: ${offset})`);
        const pageResp: AxiosResponse<ApiResponse<any>> = await this.api.get(`/cursos?${pageParams.toString()}`);
        const pageList: any[] = Array.isArray(pageResp.data?.data) ? pageResp.data.data : (Array.isArray(pageResp.data) ? (pageResp.data as any[]) : []);
        allCursos = allCursos.concat(pageList);
        console.log(`📦 getCursos - Página ${pageNum + 1} cargada, +${pageList.length} cursos. Total acumulado: ${allCursos.length}`);
      }

      console.log('📦 getCursos - COMPLETADO. Total de cursos cargados:', allCursos.length);
      return {
        success: true,
        data: allCursos,
        pagination: {
          total: allCursos.length,
          limit: allCursos.length,
          offset: 0,
        }
      } as any;
    } catch (error) {
      console.error('❌ getCursos - Error en paginación, usando fallback:', error);
      // Fallback: una sola llamada
      const resp: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/cursos${baseParams.toString() ? '?' + baseParams.toString() : ''}`);
      return resp.data;
    }
  }

  // Obtener cursos de una persona específica
  async getCursosByRut(rut: string): Promise<ApiResponse<any[]>> {
    // eslint-disable-next-line no-console
    console.log('🌐 Haciendo petición a /cursos/persona/' + rut);
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/cursos/persona/${rut}`);
      // eslint-disable-next-line no-console
      console.log('✅ Respuesta de cursos para RUT', rut, ':', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error en getCursosByRut para RUT', rut, ':', error);
      throw error;
    }
  }

  // Obtener curso específico por ID
  async getCursoById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/cursos/${id}`);
    return response.data;
  }

  // Crear nuevo curso/certificación
  async createCurso(cursoData: any): Promise<ApiResponse<any>> {
    // eslint-disable-next-line no-console
    console.log('🌐 Creando curso con datos:', JSON.stringify(cursoData, null, 2));
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/cursos', cursoData);
      // eslint-disable-next-line no-console
      console.log('✅ Curso creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error al crear curso:', error);
      // eslint-disable-next-line no-console
      console.error('❌ Datos enviados:', JSON.stringify(cursoData, null, 2));
      // eslint-disable-next-line no-console
      console.error('❌ Error response:', (error as any).response?.data);
      throw error;
    }
  }

  // Actualizar curso/certificación
  async updateCurso(id: number, cursoData: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/cursos/${id}`, cursoData);
    return response.data;
  }

  // Eliminar curso/certificación
  async deleteCurso(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/cursos/${id}`);
    return response.data;
  }

  // Obtener cursos vencidos
  async getCursosVencidos(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/cursos/vencidos');
    return response.data;
  }

  // Obtener alertas de cursos
  async getCursosAlertas(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/cursos/alertas');
    return response.data;
  }

  // Obtener cursos por vencer
  async getCursosPorVencer(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/cursos/vencer');
    return response.data;
  }

  // Obtener estadísticas de vencimiento
  async getCursosEstadisticasVencimiento(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/cursos/estadisticas-vencimiento');
    return response.data;
  }

  async getPersonalDisponibilidad(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/personal-disponible/${id}/disponibilidad`);
    return response.data;
  }

  async updatePersonalDisponibilidad(id: string, disponibilidad: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/personal-disponible/${id}/disponibilidad`, disponibilidad);
    return response.data;
  }


  // Métodos de utilidades
  async healthCheck(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/health');
    return response.data;
  }

  // ==================== MÉTODOS PARA SERVICIOS/CARTERAS ====================

  // Obtener todos los clientes de una cartera específica
  async getClientesByCartera(carteraId: string): Promise<ApiResponse<any[]>> {
    const carteraResponse = await this.getCartera(parseInt(carteraId));
    if (carteraResponse.success && carteraResponse.data?.clientes) {
      return {
        success: true,
        data: carteraResponse.data.clientes
      };
    }
    return {
      success: false,
      data: [],
      message: 'No se pudieron obtener los clientes'
    };
  }

  // ==================== MÉTODOS PARA DOCUMENTOS ====================
  
  // Obtener todos los documentos con filtros
  async getDocumentos(filters?: { 
    rut_persona?: string;
    tipo_documento?: string;
    nombre_documento?: string;
    limit?: number; 
    offset?: number; 
    search?: string 
  }): Promise<ApiResponse<any[]>> {
    // Si se especifica RUT, usar el endpoint específico
    if (filters?.rut_persona) {
      try {
        const response = await this.getDocumentosByPersona(filters.rut_persona);
        return response;
      } catch (error) {
        console.error('❌ Error obteniendo documentos por RUT:', error);
        return { success: false, data: [], message: 'Error al obtener documentos por RUT' };
      }
    }

    // Preferir endpoint unificado si está disponible: /documentos?todos=true&incluir_inactivos=true
    try {
      const params: Record<string, any> = { todos: true, incluir_inactivos: true };
      if (typeof filters?.limit !== 'undefined') params.limit = filters!.limit;
      if (typeof filters?.offset !== 'undefined') params.offset = filters!.offset;
      if (filters?.tipo_documento) params.tipo_documento = filters.tipo_documento;
      if (filters?.nombre_documento) params.nombre_documento = filters.nombre_documento;
      if (filters?.search) params.search = filters.search;

      console.log('📄 getDocumentos - Llamando a /documentos con params:', params);
      const response: AxiosResponse<any> = await this.api.get('/documentos', { params });
      const payload = response.data || {};

      // Determinar lista de documentos en la respuesta (puede venir en varios campos)
      const docs = payload.data || payload.items || payload.documentos || payload.documentos_lista || [];

      // Si la respuesta incluye métricas totales, devolverlas en pagination
      const pagination: any = {
        total: typeof payload.total === 'number' ? payload.total : docs.length,
        activos: typeof payload.activos === 'number' ? payload.activos : undefined,
        inactivos: typeof payload.inactivos === 'number' ? payload.inactivos : undefined,
        vencidos: typeof payload.vencidos === 'number' ? payload.vencidos : undefined,
        por_vencer: typeof payload.por_vencer === 'number' ? payload.por_vencer : undefined,
        vigentes: typeof payload.vigentes === 'number' ? payload.vigentes : undefined,
        limit: typeof params.limit === 'number' ? params.limit : docs.length,
        offset: typeof params.offset === 'number' ? params.offset : 0,
      };

      console.log('📄 getDocumentos - Respuesta unificada:', { total: pagination.total, docsLength: docs.length });

      return {
        success: true,
        data: Array.isArray(docs) ? docs : [],
        pagination,
      } as any;
    } catch (err) {
      console.warn('⚠️ getDocumentos - Endpoint unificado no disponible o falló, usando fallback:', (err as any)?.message);
      // Fallback a combinacion previa (vencidos + por vencer)
      try {
        const [vencidosResp, porVencerResp] = await Promise.allSettled([
          this.getDocumentosVencidos(),
          this.getDocumentosPorVencer()
        ]);

        const vencidos = vencidosResp.status === 'fulfilled' ? (vencidosResp.value?.data || []) : [];
        const porVencer = porVencerResp.status === 'fulfilled' ? (porVencerResp.value?.data || []) : [];
        const allDocs = [...vencidos, ...porVencer];
        const uniqueDocs = allDocs.filter((doc, index, self) => index === self.findIndex((d) => d.id === doc.id));

        return {
          success: true,
          data: uniqueDocs,
          pagination: { total: uniqueDocs.length, limit: uniqueDocs.length, offset: 0 }
        } as any;
      } catch (error: any) {
        console.error('❌ getDocumentos - Fallback falló:', error?.message);
        return { success: true, data: [] };
      }
    }
  }

  // Subir documento. `cargaMasiva=true` marca el request con el header X-Carga-Masiva, que
  // el backend usa para omitir la notificación de "documento subido" en cargas por lote
  // (ver routes/documentos.js) — sin eso, una carga de 200 archivos generaría 200
  // notificaciones por revisor.
  async uploadDocumento(documentoData: FormData, onProgress?: (percent: number) => void, cargaMasiva = false): Promise<ApiResponse<any>> {
    try {
      const uploadApi = axios.create({
        baseURL: API_CONFIG.BASE_URL,
        timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
        withCredentials: false,
      });

      const token = typeof window !== 'undefined' ? getTokenIfValid() : null;
      const headers: Record<string, any> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        clearSession();
        try { window.location.href = getLoginPath(); } catch (e) {}
        throw new Error('Session expired');
      }
      if (cargaMasiva) {
        headers['X-Carga-Masiva'] = '1';
      }

      const response: AxiosResponse<ApiResponse<any>> = await uploadApi.post('/documentos', documentoData, {
        headers,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error en uploadDocumento:', error.response?.status, error.response?.data?.message || error.message);
      throw error;
    }
  }

  // Obtener documento por ID
  async getDocumentoById(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/documentos/${id}`);
    return response.data;
  }

  // Obtener documentos por persona (RUT)
  async getDocumentosByPersona(rut: string): Promise<ApiResponse<any[]>> {
    // Intentar primero con el RUT tal como viene (con guión), luego sin guión como fallback
    const rutClean = rut.replace(/\./g, '').replace(/-/g, '');
    // El backend pagina con `limit = 50` por defecto y nadie acá lee `data.pagination`: sin
    // este parámetro, a una persona con muchos documentos se le perdían los más antiguos en
    // silencio (y la ficha mostraba menos de los que tiene).
    const endpoints = [`/documentos/persona/${rut}?limit=500`, `/documentos/persona/${rutClean}?limit=500`];
    let lastError: any = null;
    for (const endpoint of endpoints) {
      try {
        const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(endpoint);
        console.log(`✅ [getDocumentosByPersona] Respuesta de ${endpoint}:`, JSON.stringify(response.data).slice(0, 500));
        return response.data;
      } catch (err: any) {
        if (err?.response?.status === 404) {
          lastError = err;
          continue;
        }
        console.error(`❌ [getDocumentosByPersona] Error en ${endpoint}:`, err?.response?.status, err?.response?.data);
        lastError = err;
      }
    }
    throw lastError;
  }

  // GET /api/documentos/:id/historial - versiones anteriores (superadas) de un
  // documento vigente. Se pueden ver/descargar con visualizarDocumento/downloadDocumento
  // usando el id de cada entrada (esos endpoints ya no exigen activo=true).
  async getHistorialDocumento(id: number): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/documentos/${id}/historial`);
    return response.data;
  }

  // POST /api/documentos/:id/archivar-anterior - manda viejoId al historial como versión
  // anterior de nuevoId. El backend (archivarReemplazado) mueve al nuevo los match_manual,
  // tipos y curso del viejo, y refresca la cobertura. 400 motivo='ya_inactivo' = ya estaba.
  // prereqId: requisito desde el que se reemplazó. Si el viejo es otro papel y acredita otros
  // requisitos, el backend no lo archiva y traspasa solo lo de ese requisito (parcial: true).
  async archivarDocumentoAnterior(nuevoId: number, viejoId: number, prereqId?: number | null): Promise<ApiResponse<any> & { parcial?: boolean }> {
    const response: AxiosResponse<ApiResponse<any> & { parcial?: boolean }> = await this.api.post(
      `/documentos/${nuevoId}/archivar-anterior`, { documento_id: viejoId, prereq_id: prereqId ?? undefined }
    );
    return response.data;
  }

  // Nota: No hay endpoint específico para documentos de cursos
  // Los documentos se obtienen por persona y se filtran por tipo

  // Visualizar documento (inline, sin restricción de rol)
  async visualizarDocumento(id: number): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/documentos/${id}/visualizar`, {
      responseType: 'blob',
      timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
      headers: { 'Accept': 'application/octet-stream, application/pdf, */*' }
    });
    const contentDisposition = response.headers['content-disposition'];
    let filename = '';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) {
        try { filename = decodeURIComponent(match[1].replace(/['"]/g, '')); } catch { filename = match[1].replace(/['"]/g, ''); }
      }
    }
    return { blob: response.data, filename };
  }

  // Descargar documento
  async downloadDocumento(id: number): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log('🌐 API - Descargando documento ID:', id);
      console.log('🔗 API - URL base:', API_CONFIG.BASE_URL);
      
      // Intentar con el endpoint principal
      let response: any = undefined;
      try {
        console.log('🔍 API - Intentando con /documentos/:id/descargar');
        response = await this.api.get(`/documentos/${id}/descargar`, {
          responseType: 'blob',
          timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
          headers: {
            'Accept': 'application/octet-stream, application/pdf, */*'
          }
        });
      } catch (firstError: any) {
        // Si el error es 403 (permisos), no tiene sentido reintentar con otras rutas
        if (firstError?.response?.status === 403) {
          throw firstError;
        }

        console.warn('⚠️ API - Primer intento falló, probando rutas alternativas');

        // Solo reintentar rutas alternativas si el error fue 404 (ruta incorrecta)
        const alternativeRoutes = [
          `/documentos/${id}/download`,
          `/documentos/download/${id}`,
        ];

        let success = false;
        for (const route of alternativeRoutes) {
          try {
            console.log(`🔍 API - Intentando con ${route}`);
            response = await this.api.get(route, {
              responseType: 'blob',
              timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
              headers: {
                'Accept': 'application/octet-stream, application/pdf, */*'
              }
            });
            console.log(`✅ API - Éxito con ${route}`);
            success = true;
            break;
          } catch (altError: any) {
            if (altError?.response?.status === 403) {
              throw altError;
            }
            console.log(`❌ API - Falló con ${route}`);
            continue;
          }
        }

        if (!success || !response) {
          throw firstError;
        }
      }
      
      if (!response) {
        throw new Error('No se pudo obtener respuesta del servidor');
      }
      
      // Extraer nombre del archivo de los headers (si viene informado)
      const contentDisposition = response.headers['content-disposition'];
      let filename = '';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Decodificar si viene en UTF-8
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // Si falla la decodificación, usar el nombre tal cual
          }
        }
      }

      console.log('📁 API - Nombre del archivo (servidor):', filename);
      console.log('📦 API - Tipo de blob:', response.data.type);
      console.log('📦 API - Tamaño del blob:', response.data.size, 'bytes');
      
      if (!response.data || response.data.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }
      
      return {
        blob: response.data,
        filename: filename
      };
    } catch (error: any) {
      console.error('❌ API - Error en descarga del documento:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      throw error;
    }
  }

  // Eliminar documento
  async deleteDocumento(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/${id}`);
    return response.data;
  }

  // Eliminar documento y, si aplica, su archivo en Google Drive (intentos con fallbacks)
  async deleteDocumentoAndDrive(id: number, driveFileId?: string): Promise<ApiResponse<any>> {
    // Intento principal: pedir al backend que elimine registro y archivo en Drive en una sola llamada
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/${id}?delete_drive=1`);
      return response.data;
    } catch (primaryError: any) {
      console.warn('⚠️ Intento inicial de deleteDocumentoAndDrive falló, haciendo fallback:', primaryError?.message || primaryError);

      // Fallback 1: intentar eliminar solo el registro
      try {
        const respRegistro: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/${id}`);
        console.log('✅ Registro eliminado en backend:', respRegistro.data);
      } catch (deleteRegistroError: any) {
        console.error('❌ Error al eliminar registro de documento:', deleteRegistroError);
        // Si falla eliminar el registro, devolver el error original
        throw primaryError;
      }

      // Si no hay driveFileId, devolvemos éxito por haber eliminado el registro
      if (!driveFileId) {
        return {
          success: true,
          data: null,
          message: 'Registro eliminado. No se proporcionó drive_file_id para eliminar en Drive.'
        } as ApiResponse<any>;
      }

      // Fallback 2: intentar eliminar archivo en Drive vía endpoints alternativos del backend
      // Intento: DELETE /documentos/drive/:driveId
      try {
        const respDriveDelete: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/drive/${encodeURIComponent(driveFileId)}`);
        console.log('✅ Archivo en Drive eliminado vía /documentos/drive/:', respDriveDelete.data);
        return {
          success: true,
          data: null,
          message: 'Registro y archivo en Drive eliminados (vía /documentos/drive/:id)'
        } as ApiResponse<any>;
      } catch (driveDeleteError1: any) {
        console.warn('⚠️ Eliminación en Drive vía /documentos/drive/:id falló:', driveDeleteError1?.message || driveDeleteError1);
      }

      // Intento: POST /documentos/drive-delete { drive_file_id }
      try {
        const respDriveDelete2: AxiosResponse<ApiResponse<any>> = await this.api.post('/documentos/drive-delete', { drive_file_id: driveFileId });
        console.log('✅ Archivo en Drive eliminado vía /documentos/drive-delete:', respDriveDelete2.data);
        return respDriveDelete2.data;
      } catch (driveDeleteError2: any) {
        console.warn('⚠️ Eliminación en Drive vía /documentos/drive-delete falló:', driveDeleteError2?.message || driveDeleteError2);
        // Finalmente retornamos éxito parcial: registro eliminado, pero archivo en Drive no pudo ser eliminado por los endpoints disponibles
        return {
          success: false,
          data: null,
          message: 'Registro eliminado, pero no se pudo eliminar el archivo en Drive. Por favor elimínelo manualmente si es necesario.'
        } as ApiResponse<any>;
      }
    }
  }

  // Actualizar documento
  async updateDocumento(id: number, data: any): Promise<ApiResponse<any>> {
    // Si hay un archivo, usar FormData
    if (data.archivo) {
      const formData = new FormData();

      // Agregar campos de texto
      if (data.nombre_documento) formData.append('nombre_documento', data.nombre_documento);
      if (data.tipo_documento) formData.append('tipo_documento', data.tipo_documento);
      if (data.fecha_emision) formData.append('fecha_emision', data.fecha_emision);
      if (data.fecha_vencimiento) formData.append('fecha_vencimiento', data.fecha_vencimiento);
      if (data.dias_validez) formData.append('dias_validez', data.dias_validez.toString());
      if (data.estado_documento) formData.append('estado_documento', data.estado_documento);
      if (data.institucion_emisora) formData.append('institucion_emisora', data.institucion_emisora);
      if (data.observaciones) formData.append('observaciones', data.observaciones);

      // Agregar archivo
      formData.append('archivo', data.archivo);

      // Indicar al backend el nombre final deseado para sobreescribir/guardar el archivo
      if (data.nombre_documento) {
        formData.append('nombre_archivo_destino', data.nombre_documento);
      }

      // Añadir carpeta destino inferida a partir del tipo de documento si no viene explícita
      try {
        const inferred = mapTipoDocumentoToFolder(data.tipo_documento || null);
        const carpetaDestino = (data as any).carpeta_destino || inferred;
        if (carpetaDestino) formData.append('carpeta_destino', carpetaDestino);
      } catch (e) {
        // no bloquear si falla la inferencia
      }

      // Vínculo cliente/prerrequisito: mismo formato que createDocumentoFormData, para que
      // el PUT pueda mantener (o corregir) el match manual del documento que se edita.
      if (data.cliente_id !== undefined && data.cliente_id !== null && String(data.cliente_id).trim() !== '') {
        formData.append('cliente_id', String(data.cliente_id));
      }
      if (data.prerrequisitos) {
        try {
          formData.append('prerrequisitos', JSON.stringify(data.prerrequisitos));
          if (Array.isArray(data.prerrequisitos)) {
            data.prerrequisitos.forEach((pid: any) => formData.append('prerrequisitos[]', String(pid)));
          }
        } catch (e) {
          console.warn('No se pudo serializar prerrequisitos:', e);
        }
      }

      // Debug: Log de todos los campos del FormData (útil para diagnosticar 400)
      try {
        console.log('🔍 Enviando FormData (updateDocumento):');
        const entries = Array.from(formData.entries());
        entries.forEach(([key, value]) => {
          if (value instanceof File) {
            console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
          } else {
            console.log(`  ${key}: ${value}`);
          }
        });
      } catch (e) {
        console.warn('No se pudo listar FormData para debug:', e);
      }

      // `this.api` tiene 'Content-Type: application/json' por defecto y axios v1 serializa un
      // FormData a JSON cuando ve ese header: el archivo llegaba como {} y el backend solo
      // actualizaba fechas, sin reemplazar el archivo ni dejar el anterior en el historial.
      // Con 'multipart/form-data' axios lo deja pasar y el navegador pone el boundary.
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/documentos/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
      });
      return response.data;
    } else {
      // Si no hay archivo, enviar como JSON
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/documentos/${id}`, data);
      return response.data;
    }
  }

  // Obtener tipos de documentos disponibles
  async getTiposDocumentos(): Promise<ApiResponse<any[]>> {
    console.log('🌐 Haciendo petición a /documentos/tipos');
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/documentos/tipos');
      console.log('✅ Respuesta de tipos de documentos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error en getTiposDocumentos:', error);
      throw error;
    }
  }

  // Obtener todos los documentos con código de verificación (zc_temp.verificacion_config)
  async getDocumentosVerificacion(incluirInactivos = false): Promise<ApiResponse<any[]>> {
    try {
      const params: Record<string, any> = {};
      if (incluirInactivos) params.incluir_inactivos = 'true';
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/verificacion-temp/list-full', { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error en getDocumentosVerificacion:', error);
      throw error;
    }
  }

  // Obtener formatos de archivo soportados
  async getFormatosArchivo(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/documentos/formatos');
    return response.data;
  }

  // ==================== CLONACIÓN DE DOCUMENTOS ====================
  
  // Clonar un documento (crear registro que apunta al mismo archivo)
  async clonarDocumento(id: number, data: { tipo_documento: string; descripcion?: string; fecha_vencimiento?: string }): Promise<ApiResponse<any>> {
    console.log(`🔄 Clonando documento ${id}:`, data);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/documentos/${id}/clonar`, data);
      console.log('✅ Documento clonado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al clonar documento:', error);
      throw error;
    }
  }

  // Obtener lista de clones de un documento
  async getClonesDocumento(id: number): Promise<ApiResponse<any>> {
    console.log(`📋 Obteniendo clones del documento ${id}`);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/documentos/${id}/clones`);
      console.log('✅ Clones obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener clones:', error);
      throw error;
    }
  }

  // Eliminar documento con opción de eliminar clones
  async deleteDocumentoWithClones(id: number, eliminarClones: boolean = false): Promise<ApiResponse<any>> {
    console.log(`🗑️ Eliminando documento ${id}, eliminar_clones:`, eliminarClones);
    try {
      const params = eliminarClones ? '?eliminar_clones=true' : '';
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/${id}${params}`);
      console.log('✅ Documento eliminado:', response.data);
      return response.data;
    } catch (error: any) {
      // Logs detallados para depuración
      try {
        console.error('❌ Error al eliminar documento - Detalles:');
        console.error('Request config:', {
          method: error.config?.method,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          params: error.config?.params,
          headers: error.config?.headers,
          data: error.config?.data,
        });
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
          console.error('Response data:', error.response.data);
        } else if (error.request) {
          console.error('No response received. Request object:', error.request);
        }
      } catch (logErr) {
        console.error('Error while logging deleteDocumentoWithClones error:', logErr);
      }
      // Si se pidió eliminar clones y la petición con query param falló,
      // intentar un fallback: eliminar sin el parámetro (por compatibilidad con backends sin sistema de clones)
      if (eliminarClones) {
        try {
          console.warn(`⚠️ Fallback: retrying plain delete for documento ${id} without eliminar_clones param`);
          const fallbackResp: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/documentos/${id}`);
          console.log('✅ Fallback delete succeeded:', fallbackResp.data);
          return fallbackResp.data;
        } catch (fallbackErr: any) {
          console.error('❌ Fallback delete also failed:', fallbackErr);
          // adjuntar info de fallback al error original para diagnóstico
          (error as any).fallbackError = fallbackErr;
        }
      }

      // Si el error es 400 con HAS_CLONES, propagar la información de clones
      if (error.response?.status === 400 && error.response?.data?.error === 'HAS_CLONES') {
        console.warn('⚠️ El documento tiene clones:', error.response.data);
        const cloneError = new Error('El documento tiene clones');
        (cloneError as any).hasClones = true;
        (cloneError as any).totalClones = error.response.data.total_clones;
        (cloneError as any).clones = error.response.data.clones;
        (cloneError as any).originalError = error;
        throw cloneError;
      }

      // Re-lanzar con más contexto en el mensaje
      const wrapped = new Error(`Error deleting documento ${id}: ${error.message}`);
      (wrapped as any).original = error;
      throw wrapped;
    }
  }

  // ==================== MÉTODOS PARA ÁREA DE SERVICIO ====================
  
  // Obtener personal del área de servicio
  async getAreaServicioPersonal(filters?: { 
    cargo?: string;
    zona?: string;
    limit?: number; 
    offset?: number; 
    search?: string 
  }): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (filters?.cargo) params.append('cargo', filters.cargo);
    if (filters?.zona) params.append('zona', filters.zona);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.search) params.append('search', filters.search);
    
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/area-servicio${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  }

  // Obtener estadísticas del área de servicio
  async getAreaServicioStats(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/area-servicio/stats');
    return response.data;
  }

  // Obtener cargos disponibles
  async getCargosDisponibles(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/area-servicio/cargos');
    return response.data;
  }

  // Obtener zonas geográficas
  async getZonasGeograficas(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/area-servicio/zonas');
    return response.data;
  }

  // Obtener personal por cargo
  async getPersonalByCargo(cargo: string): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/area-servicio/cargo/${cargo}`);
    return response.data;
  }

  // Obtener personal por zona
  async getPersonalByZona(zona: string): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/area-servicio/zona/${zona}`);
    return response.data;
  }

  // Obtener personal disponible para servicio
  async getPersonalDisponibleServicio(): Promise<ApiResponse<any[]>> {
    const normalizeItem = (item: any) => ({
      ...item,
      display_name: item?.display_name || [item?.nombre, item?.apellido].filter(Boolean).join(' ').trim() || item?.nombres || item?.rut || ''
    });

    try {
      const batchSize = 100;
      const firstResponse: AxiosResponse<any> = await this.api.get('/personal-disponible', {
        params: {
          estado_id: 1,
          limit: batchSize,
          offset: 0,
        }
      });

      const firstPayload = firstResponse.data || {};
      const firstItems: any[] = Array.isArray(firstPayload.data)
        ? firstPayload.data
        : Array.isArray(firstPayload.items)
          ? firstPayload.items
          : Array.isArray(firstPayload)
            ? firstPayload
            : [];

      const totalRecords = firstPayload.pagination?.total ?? firstPayload.pagination?.count ?? firstItems.length;
      let allItems = firstItems.map(normalizeItem);

      if (totalRecords > allItems.length) {
        const totalPages = Math.ceil(totalRecords / batchSize);

        for (let pageNum = 1; pageNum < totalPages; pageNum++) {
          const offset = pageNum * batchSize;
          const pageResp: AxiosResponse<any> = await this.api.get('/personal-disponible', {
            params: {
              estado_id: 1,
              limit: batchSize,
              offset,
            }
          });

          const pagePayload = pageResp.data || {};
          const pageItems: any[] = Array.isArray(pagePayload.data)
            ? pagePayload.data
            : Array.isArray(pagePayload.items)
              ? pagePayload.items
              : Array.isArray(pagePayload)
                ? pagePayload
                : [];

          allItems = allItems.concat(pageItems.map(normalizeItem));
        }
      }

      return {
        success: true,
        data: allItems,
        pagination: {
          total: totalRecords,
          limit: batchSize,
          offset: 0,
        }
      } as any;
    } catch (error: any) {
      console.warn('⚠️ getPersonalDisponibleServicio - fallo V2, intentando fallback legacy:', error?.message || error);
      try {
        const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/area-servicio/disponibles');
        const legacyData = Array.isArray(response.data?.data) ? response.data.data : [];
        return {
          ...response.data,
          data: legacyData.map(normalizeItem)
        } as any;
      } catch (legacyError) {
        console.error('❌ getPersonalDisponibleServicio - fallback legacy falló:', legacyError);
        throw error;
      }
    }
  }


  // ==================== MÉTODOS PARA BACKUP ====================
  
  // Obtener lista de backups
  async getBackups(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/backup');
    return response.data;
  }

  // Crear nuevo backup
  async createBackup(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/backup');
    return response.data;
  }

  // Descargar backup específico
  async downloadBackup(filename: string): Promise<Blob> {
    const response = await this.api.get(`/backup/${filename}`, {
      responseType: 'blob',
      timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
    });
    return response.data;
  }

  // Eliminar backup específico
  async deleteBackup(filename: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/backup/${filename}`);
    return response.data;
  }

  // Obtener información del sistema de backups
  async getBackupInfo(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/backup/info');
    return response.data;
  }

  // Método para obtener estadísticas del dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      const response: AxiosResponse<ApiResponse<DashboardStats>> = await this.api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas del dashboard:', error);
      throw error;
    }
  }

  // ==================== MÉTODOS PARA IMÁGENES DE PERFIL ====================
  
  // Subir imagen de perfil
  async uploadProfileImage(rut: string, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use the main axios instance (this.api) so interceptors (Authorization) are applied.
      // Provide a longer timeout for uploads via request config.
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/personal/${rut}/upload`, formData, {
        headers: {
          // Let axios set the multipart boundary automatically, do not set full Content-Type string
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 seconds for profile image uploads
      });

      console.log('✅ Imagen de perfil subida exitosamente:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error al subir imagen de perfil:', error);
      throw error;
    }
  }

  // ==================== MÉTODOS ALTERNATIVOS PARA PROFILE-PHOTOS (backend variant) ====================

  // POST /api/profile-photos/:rut/upload
  async uploadProfilePhoto(rut: string, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/profile-photos/${rut}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      console.log('✅ uploadProfilePhoto response:', response.data);
      return response.data;
    } catch (error: any) {
      console.warn('⚠️ uploadProfilePhoto failed, will surface error to caller:', error?.message || error);
      throw error;
    }
  }

  // GET /api/profile-photos/:rut/image
  async getProfilePhoto(rut: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/profile-photos/${rut}/image`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { success: false, message: 'No se encontró imagen de perfil', data: null };
      }
      console.warn('⚠️ getProfilePhoto failed for RUT:', rut, error?.message || error);
      return { success: false, message: 'Error al obtener imagen de perfil', data: null };
    }
  }

  // GET /api/profile-photos/:rut/image/download
  async downloadProfilePhoto(rut: string): Promise<{ blob: Blob; filename: string }> {
    const response: any = await this.api.get(`/profile-photos/${rut}/image/download`, { responseType: 'blob' });
    const contentDisposition = response.headers['content-disposition'];
    let filename = `profile_${rut}.png`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) filename = match[1].replace(/['"]/g, '');
    }
    return { blob: response.data, filename };
  }

  // DELETE /api/profile-photos/:rut/image
  async deleteProfilePhoto(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/profile-photos/${rut}/image`);
    return response.data;
  }

  // GET /api/users/me/photo (convenience endpoint for authenticated user)
  async getUsersMePhoto(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/users/me/photo');
    return response.data;
  }

  // Obtener imagen de perfil
  async getProfileImage(rut: string): Promise<ApiResponse<any>> {
    try {
      const url = `/personal/${rut}/image/download`;
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(url);
      return response.data;
    } catch (error: any) {
      // Si es un error 404, significa que no hay imagen de perfil, esto es normal
      if (error.response?.status === 404) {
        // No loguear este error ya que es esperado cuando no hay imagen
        return {
          success: false,
          message: 'No se encontró imagen de perfil',
          data: null
        };
      }
      // Para otros errores, los mostramos como warnings, no como errores críticos
      console.warn('⚠️ No se pudo obtener imagen de perfil para RUT:', rut, error.message);
      return {
        success: false,
        message: 'Error al obtener imagen de perfil',
        data: null
      };
    }
  }

  // Verificar si existe imagen de perfil sin generar errores 404 en consola
  async checkProfileImageExists(rut: string): Promise<boolean> {
    try {
      const response = await this.api.head(`/personal/${rut}/image`);
      return response.status === 200;
    } catch (error: any) {
      // Si es 404, no existe la imagen
      if (error.response?.status === 404) {
        return false;
      }
      // Para otros errores, asumir que no existe
      return false;
    }
  }

  // Descargar imagen de perfil
  async downloadProfileImage(rut: string): Promise<Blob> {
    try {
      const response = await this.api.get(`/personal/${rut}/image`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error al descargar imagen de perfil:', error);
      throw error;
    }
  }

  // Eliminar imagen de perfil
  async deleteProfileImage(rut: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/personal/${rut}/image`);
      console.log('✅ Imagen de perfil eliminada:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error al eliminar imagen de perfil:', error);
      throw error;
    }
  }

  // ==================== MÉTODOS PARA PROGRAMACIÓN/EVENTOS ====================
  
  async getEventos(queryParams?: string): Promise<PaginatedResponse<any[]>> {
    try {
      const response = await this.api.get(`/programacion/eventos${queryParams ? `?${queryParams}` : ''}`);
      return {
        success: true,
        data: response.data.data || response.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al obtener eventos:', error);
      return {
        success: false,
        data: [],
        message: 'Error al obtener eventos',
        pagination: { total: 0, offset: 0, limit: 10 }
      };
    }
  }

  async getEventoById(eventoId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get(`/programacion/eventos/${eventoId}`);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al obtener evento:', error);
      return {
        success: false,
        data: null,
        message: 'Error al obtener evento'
      };
    }
  }

  async createEvento(eventoData: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/programacion/eventos', eventoData);
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Evento creado exitosamente'
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al crear evento:', error);
      return {
        success: false,
        data: null,
        message: 'Error al crear evento'
      };
    }
  }

  async updateEvento(eventoId: string, eventoData: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.put(`/programacion/eventos/${eventoId}`, eventoData);
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Evento actualizado exitosamente'
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al actualizar evento:', error);
      return {
        success: false,
        data: null,
        message: 'Error al actualizar evento'
      };
    }
  }

  async deleteEvento(eventoId: string): Promise<ApiResponse<null>> {
    try {
      await this.api.delete(`/programacion/eventos/${eventoId}`);
      return {
        success: true,
        data: null,
        message: 'Evento eliminado exitosamente'
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al eliminar evento:', error);
      return {
        success: false,
        data: null,
        message: 'Error al eliminar evento'
      };
    }
  }

  async getProgramacionStats(): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get('/programacion/stats');
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al obtener estadísticas de programación:', error);
      return {
        success: false,
        data: null,
        message: 'Error al obtener estadísticas'
      };
    }
  }

  // ==================== MÉTODOS PARA SERVICIOS ====================
  
  // Carteras
  async getCarteras(params?: { limit?: number; offset?: number; search?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/carteras', { params });
    return response.data;
  }

  async getCartera(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/carteras/${id}`);
    return response.data;
  }

  async createCartera(data: { name: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/carteras', data);
    return response.data;
  }

  // Clientes
  // `simple: true` pide la versión liviana (id, nombre, cartera_id, visible_en_chart): es lo
  // único que necesita un selector y evita bajar 228 KB de conteos de nodos y arreglos de
  // carteras que ese combo nunca mira.
  async getClientes(params?: { limit?: number; offset?: number; search?: string; cartera_id?: number; solo_servicio?: boolean; simple?: boolean; visible_en_chart?: boolean }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/clientes', { params });
    return response.data;
  }

  async getCliente(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/clientes/${id}`);
    return response.data;
  }

  async createCliente(data: { nombre: string; cartera_id: number; region_id?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/clientes', data);
    return response.data;
  }

  // GET /servicios/clientes/:id/prerrequisitos
  async getClientePrerrequisitosByClienteId(clienteId: number): Promise<ApiResponse<any[]>> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/servicios/clientes/${clienteId}/prerrequisitos`);
      const raw = response.data;
      const data = Array.isArray(raw?.data) ? raw.data
        : Array.isArray((raw as any)?.data?.data) ? (raw as any).data.data
        : Array.isArray(raw) ? raw as any
        : null;
      if (data !== null) return { ...raw, data } as ApiResponse<any[]>;
    } catch (e: any) {
      if (e?.response?.status !== 404) throw e;
      // 404 → endpoint no desplegado aún, caemos al fallback
    }
    return this.getPrerrequisitosByCliente(clienteId);
  }

  // POST /servicios/clientes/:id/prerrequisitos
  // TODO: usar ruta V2 cuando el backend la despliegue:
  //   this.api.post(`/servicios/clientes/${clienteId}/prerrequisitos`, data)
  async crearPrerrequisitoV2(clienteId: number, data: { nombre: string; obligatorio?: boolean; dias_validez?: number | null }): Promise<ApiResponse<any>> {
    return this.crearPrerrequisito({
      cliente_id: clienteId,
      tipo_documento: data.nombre,
      dias_duracion: data.dias_validez ?? null,
    });
  }

  // PUT /servicios/clientes/:id/prerrequisitos/:prereqId
  // TODO: usar ruta V2 cuando el backend la despliegue:
  //   this.api.put(`/servicios/clientes/${clienteId}/prerrequisitos/${prereqId}`, data)
  async actualizarPrerrequisitoV2(clienteId: number, prereqId: number, data: { nombre?: string; obligatorio?: boolean; dias_validez?: number | null }): Promise<ApiResponse<any>> {
    return this.actualizarPrerrequisito(prereqId, {
      tipo_documento: data.nombre,
      dias_duracion: data.dias_validez ?? null,
    });
  }

  // DELETE /servicios/clientes/:id/prerrequisitos/:prereqId
  // TODO: usar ruta V2 cuando el backend la despliegue:
  //   this.api.delete(`/servicios/clientes/${clienteId}/prerrequisitos/${prereqId}`)
  async eliminarPrerrequisitoV2(clienteId: number, prereqId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/requisitos/${prereqId}`);
    return response.data;
  }

  // PATCH /servicios/clientes/:id/es-servicio
  async patchClienteEsServicio(clienteId: number, esServicio: boolean): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/servicios/clientes/${clienteId}/es-servicio`, { es_servicio: esServicio });
      return response.data;
    } catch (e: any) {
      if (e?.response?.status !== 404) throw e;
      // 404 → endpoint no desplegado, devolvemos ok para que el caller use localStorage
      return { success: false, data: null, message: 'endpoint_not_deployed' } as any;
    }
  }

  // PATCH /servicios/clientes/:id/visible-chart
  // Controla la visibilidad del cliente en el chart de VisualizacionPage (independiente de es_servicio).
  async patchClienteVisibleChart(clienteId: number, visible: boolean): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/servicios/clientes/${clienteId}/visible-chart`, { visible_en_chart: visible });
    return response.data;
  }

  // Visualizacion: custom groups persistence (frontend uses these to store named client groups)
  async getVisualGroups(): Promise<ApiResponse<any[]>> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/servicios/visualizacion/grupos');
      return response.data;
    } catch (error) {
      // Preserve existing error handling strategy used elsewhere
      // eslint-disable-next-line no-console
      console.warn('Error fetching visual groups:', error);
      return { success: false, data: null, message: 'Error fetching visual groups' } as any;
    }
  }

  async createVisualGroup(payload: { nombre: string; keys: string[] }): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/visualizacion/grupos', payload);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error creating visual group:', error);
      return { success: false, data: null, message: 'Error creating visual group' } as any;
    }
  }

  async deleteVisualGroup(id: string | number): Promise<ApiResponse<null>> {
    try {
      const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/servicios/visualizacion/grupos/${id}`);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error deleting visual group:', error);
      return { success: false, data: null, message: 'Error deleting visual group' } as any;
    }
  }

  // Método de subida de documentos para cliente
  async updateMetodoSubidaCliente(clienteId: number, data: {
    metodo_subida_documentos: string;
    config_subida_documentos?: any;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/servicios/clientes/${clienteId}/metodo-subida`,
      data
    );
    return response.data;
  }

  async getHistorialMetodoSubidaCliente(clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/servicios/clientes/${clienteId}/historial-metodo-subida`
    );
    return response.data;
  }

  // Nodos
  async getNodos(params?: { limit?: number; offset?: number; search?: string; cliente_id?: number; cartera_id?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/nodos', { params });
    return response.data;
  }

  async getNodo(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/nodos/${id}`);
    return response.data;
  }

  async createNodo(data: { nombre: string; cliente_id: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/nodos', data);
    return response.data;
  }

  // PUT /api/carteras/:id - Actualizar nombre de cartera
  // ⚠️ IMPORTANTE: usa "name", no "nombre"
  async updateCartera(carteraId: number, data: { name?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/carteras/${carteraId}`, data);
    return response.data;
  }

  // PUT /api/clientes/:id - Actualizar cliente
  async updateCliente(clienteId: number, data: { nombre?: string; cartera_id?: number; region_id?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/clientes/${clienteId}`, data);
    return response.data;
  }

  // PUT /api/nodos/:id - Actualizar nodo
  async updateNodo(nodoId: number, data: { nombre?: string; cliente_id?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/nodos/${nodoId}`, data);
    return response.data;
  }

  // DELETE /carteras/:id - Eliminar cartera (el backend la expone SIN prefijo /servicios)
  async deleteCartera(carteraId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/carteras/${carteraId}`);
    return response.data;
  }

  // DELETE /servicios/clientes/:id - Eliminar cliente
  // NOTA: el recurso individual de cliente vive en /servicios/clientes/:id (GET funciona),
  // pero el backend AÚN NO registra el verbo DELETE ahí → devuelve "Endpoint no encontrado".
  // Pendiente backend: implementar DELETE /servicios/clientes/:id.
  async deleteCliente(clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicios/clientes/${clienteId}`);
    return response.data;
  }

  // DELETE /servicios/nodos/:id - Eliminar nodo
  async deleteNodo(nodoId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicios/nodos/${nodoId}`);
    return response.data;
  }

  // GET /servicios/nodos-meta - listado plano de responsables IA/IS por nodo (zc_temp.nodo_meta)
  async getNodosMeta(): Promise<ApiResponse<Array<{ nodo_id: string; ia: { rut: string; nombre?: string | null } | null; is: { rut: string; nombre?: string | null } | null }>>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/nodos-meta');
    return response.data;
  }

  // PUT /servicios/nodos/:id/meta - asignar responsables IA/IS de un nodo
  async setNodoMeta(nodoId: number | string, data: { ia_rut?: string; is_rut?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/nodos/${nodoId}/meta`, data);
    return response.data;
  }

  // GET /servicios/clientes-meta - listado plano de responsables IA/IS por cliente (zc_temp.cliente_meta)
  async getClientesMeta(): Promise<ApiResponse<Array<{ cliente_id: string; ia: { rut: string; nombre?: string | null } | null; is: { rut: string; nombre?: string | null } | null }>>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/clientes-meta');
    return response.data;
  }

  // PUT /servicios/clientes/:id/meta - asignar responsables IA/IS de un cliente
  async setClienteMeta(clienteId: number | string, data: { ia_rut?: string; is_rut?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/clientes/${clienteId}/meta`, data);
    return response.data;
  }

  // ==================== PERTENENCIA CARTERA ↔ CLIENTE (muchos-a-muchos) ====================
  // Un cliente es fijo (1 registro) pero puede pertenecer a varias carteras. La pertenencia
  // efectiva = clientes.cartera_id (principal) ∪ cartera_clientes (extras). Ver
  // docs/BACKEND_CARTERA_CLIENTES_FLEXIBLE.md

  // GET /servicios/carteras/:id/clientes → cada cliente trae es_principal
  async getClientesDeCartera(carteraId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/carteras/${carteraId}/clientes`);
    return response.data;
  }

  // POST /servicios/carteras/:id/clientes {cliente_id} → 201, o 200 idempotente si ya existe
  async agregarClienteACartera(carteraId: number, clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/servicios/carteras/${carteraId}/clientes`, { cliente_id: clienteId });
    return response.data;
  }

  // DELETE /servicios/carteras/:id/clientes/:clienteId → 409 si es la única cartera del cliente
  async quitarClienteDeCartera(carteraId: number, clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicios/carteras/${carteraId}/clientes/${clienteId}`);
    return response.data;
  }

  // ==================== TRASPASOS EN SERVICIOS ====================
  // Ver docs/BACKEND_TRASPASOS_SERVICIOS.md. Mover cliente = cambiar su cartera PRINCIPAL
  // (re-vincula el personal de la cartera origen). Traspasar nodos = reasignar cliente_id.

  // PUT /servicios/clientes/:id/cartera { cartera_id, mantener_anterior } → mover cliente (cambia principal)
  async moverClienteACartera(clienteId: number, carteraId: number, mantenerAnterior = false): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/servicios/clientes/${clienteId}/cartera`,
      { cartera_id: carteraId, mantener_anterior: mantenerAnterior }
    );
    return response.data;
  }

  // PUT /servicios/clientes/cartera-masiva { cliente_ids[], cartera_id, mantener_anterior } → mover varios clientes
  async moverClientesACarteraMasivo(clienteIds: number[], carteraId: number, mantenerAnterior = false): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/servicios/clientes/cartera-masiva`,
      { cliente_ids: clienteIds, cartera_id: carteraId, mantener_anterior: mantenerAnterior }
    );
    return response.data;
  }

  // GET /servicios/nodos/transferir/preview?nodo_ids=...&cliente_id_destino=... → solo lectura, impacto en personal
  async getTraspasoNodosPreview(nodoIds: Array<number | string>, clienteIdDestino: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/nodos/transferir/preview`, {
      params: { nodo_ids: nodoIds.join(','), cliente_id_destino: clienteIdDestino },
    });
    return response.data;
  }

  // PUT /servicios/nodos/transferir { nodo_ids[], cliente_id_destino } → traspaso masivo de nodos
  async transferirNodos(nodoIds: Array<number | string>, clienteIdDestino: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/nodos/transferir`, {
      nodo_ids: nodoIds.map((n) => Number(n)),
      cliente_id_destino: clienteIdDestino,
    });
    return response.data;
  }

  // PUT /servicios/nodos/:id/cliente { cliente_id } → traspaso individual de un nodo
  async transferirNodo(nodoId: number, clienteIdDestino: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/nodos/${nodoId}/cliente`, {
      cliente_id: clienteIdDestino,
    });
    return response.data;
  }

  // Zonas de gestión (catálogo zc_temp.zonas_gestion) — agrupan carteras
  // GET /carteras/zonas → [{ id, nombre, orden, color, carteras:[{id,nombre,total_clientes}] }] + grupo "Sin zona" (id:null)
  async getZonasGestion(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/carteras/zonas');
    return response.data;
  }

  // PUT /carteras/:id/zona { zona } → zona = NOMBRE del catálogo, o null para quitar la asignación
  async setCarteraZona(carteraId: number, zona: string | null): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/carteras/${carteraId}/zona`, { zona });
    return response.data;
  }


  // Estructura y Estadísticas
  async getEstructura(params?: { cartera_id?: number; cliente_id?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/estructura', { params });
    return response.data;
  }

  async getEstadisticasServicios(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/servicios/estadisticas');
    return response.data;
  }

  // ==================== MÉTODOS PARA ASIGNACIONES ====================
  // Base: http://192.168.10.194:3000/api/asignaciones (compartimos el mismo baseURL configurado)

  // Obtener asignaciones por RUT
  async getAsignacionesByRut(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/asignaciones/persona/${rut}`);
    return response.data;
  }

  // Crear asignación a cartera (idempotente)
  async assignCarteraToPersona(rut: string, carteraId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/asignaciones/persona/${rut}/carteras`, { cartera_id: carteraId });
    return response.data;
  }

  // Eliminar asignación de cartera
  async unassignCarteraFromPersona(rut: string, carteraId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/asignaciones/persona/${rut}/carteras/${carteraId}`);
    return response.data;
  }

  // Crear asignación a cliente (idempotente)
  async assignClienteToPersona(rut: string, clienteId: number, options?: { enforce?: boolean }): Promise<ApiResponse<any>> {
    const payload: any = { cliente_id: clienteId };
    if (options && typeof options.enforce !== 'undefined') payload.enforce = options.enforce;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/asignaciones/persona/${rut}/clientes`, payload);
    return response.data;
  }

  // Eliminar asignación de cliente
  async unassignClienteFromPersona(rut: string, clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/asignaciones/persona/${rut}/clientes/${clienteId}`);
    return response.data;
  }

  // Crear asignación a nodo (idempotente)
  async assignNodoToPersona(rut: string, nodoId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/asignaciones/persona/${rut}/nodos`, { nodo_id: nodoId });
    return response.data;
  }

  // Eliminar asignación de nodo
  async unassignNodoFromPersona(rut: string, nodoId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/asignaciones/persona/${rut}/nodos/${nodoId}`);
    return response.data;
  }

  // Listados inversos
  async getPersonalByCartera(id: number): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/asignaciones/carteras/${id}/personal`);
    return response.data;
  }

  async getPersonalByCliente(id: number): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/asignaciones/clientes/${id}/personal`);
    return response.data;
  }

  async getPersonalByNodo(id: number): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/asignaciones/nodos/${id}/personal`);
    return response.data;
  }
 
  // ==================== MÉTODOS PARA REQUISITOS/PRERREQUISITOS DE CLIENTE ====================
  // GET /requisitos/clientes/:clienteId  (fallback to /prerrequisitos/...)
  async getClientePrerequisitos(clienteId: number): Promise<ApiResponse<any>> {
    const resp = await this.getWithLegacy(`/prerrequisitos/clientes/${clienteId}`, `/requisitos/clientes/${clienteId}`);
    return resp.data;
  }

  // GET /prerrequisitos/clientes/:clienteId/cumplen
  // Devuelve personas que cumplen total o parcialmente los prerrequisitos para el cliente
  // Ver docs/FRONTEND_ENDPOINT_CUMPLEN.md para el detalle de parámetros soportados.
  async getClienteCumplen(
    clienteId: number,
    params?: {
      includeGlobal?: boolean;
      limit?: number;
      offset?: number;
      ignore_vencidos?: boolean;
      allowReuse?: boolean;
      onlyCompletos?: boolean;
      max_missing?: number;
      min_provided?: number;
      min_valid?: number;
    }
  ): Promise<ApiResponse<any>> {
    const query: any = {};

    if (typeof params !== 'undefined' && params !== null) {
      // Copiar solo claves definidas para evitar enviar "undefined" al backend
      const allowedKeys: (keyof typeof params)[] = [
        'includeGlobal',
        'limit',
        'offset',
        'ignore_vencidos',
        'allowReuse',
        'onlyCompletos',
        'max_missing',
        'min_provided',
        'min_valid',
      ];

      for (const key of allowedKeys) {
        const value = params[key];
        if (typeof value !== 'undefined' && value !== null) {
          query[key] = value;
        }
      }
    }

    const resp = await this.getWithLegacy(
      `/requisitos/clientes/${clienteId}/cumplen`,
      `/prerrequisitos/clientes/${clienteId}/cumplen`,
      { params: query }
    );
    return resp.data;
  }

  // POST /prerrequisitos/clientes/:clienteId { requisitos: [...] }
  async upsertClientePrerequisitos(clienteId: number, requisitos: Array<{ tipo_documento: string; obligatorio: boolean; dias_validez?: number }>): Promise<ApiResponse<any>> {
    const resp = await this.postWithLegacy(`/requisitos/clientes/${clienteId}`, `/prerrequisitos/clientes/${clienteId}`, { requisitos });
    return resp.data;
  }

  // GET /prerrequisitos/clientes/:clienteId/match?rut=:rut
  async matchPrerequisitosCliente(clienteId: number, rut: string): Promise<ApiResponse<any>> {
    const verbose = String(process.env.REACT_APP_PREREQ_VERBOSE_LOG || '').toLowerCase() === 'true';
    const tag = '[prereq-match]';
    try {
      if (verbose) console.debug(`${tag} Attempting server match for clienteId=${clienteId} rut=${rut}`);
      const response: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(
        `/requisitos/clientes/${clienteId}/match`,
        `/prerrequisitos/clientes/${clienteId}/match`,
        { params: { rut } }
      );
      if (verbose) console.debug(`${tag} Server match succeeded for clienteId=${clienteId} rut=${rut}`);
      return response.data;
    } catch (err: any) {
      const status = err?.response?.status;
      if (verbose) console.debug(`${tag} Server match failed with status=${status} for clienteId=${clienteId} rut=${rut}`);

      // Si no existe endpoint de match en backend (404), intentar varios fallbacks.
      if (status === 404) {
        // Try direct detail endpoint which some backends implement:
        // GET /prerrequisitos/clientes/:clienteId/faltantes/:rut
        try {
          if (verbose) console.debug(`${tag} Trying direct detail endpoint: GET /requisitos/clientes/${clienteId}/faltantes/${rut} (with legacy fallback)`);
          const detailResp: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(
            `/requisitos/clientes/${clienteId}/faltantes/${rut}`,
            `/prerrequisitos/clientes/${clienteId}/faltantes/${rut}`
          );
          if (verbose) console.debug(`${tag} direct detail succeeded for clienteId=${clienteId} rut=${rut}`);
          return detailResp.data;
        } catch (detailErr: any) {
          if (verbose) console.debug(`${tag} direct detail failed: ${detailErr?.response?.status || detailErr?.message || detailErr}`);
        }
        // Fallback 1: algunos servidores aceptan el match como query sobre el recurso base
        try {
          if (verbose) console.debug(`${tag} Trying alt1: GET /requisitos/clientes/${clienteId}?rut=${rut}&match=1 (with legacy fallback)`);
          const alt1: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(
            `/requisitos/clientes/${clienteId}`,
            `/prerrequisitos/clientes/${clienteId}`,
            { params: { rut, match: 1 } }
          );
          if (verbose) console.debug(`${tag} alt1 succeeded for clienteId=${clienteId} rut=${rut}`);
          return alt1.data;
        } catch (err2: any) {
          if (verbose) console.debug(`${tag} alt1 failed: ${err2?.response?.status || err2?.message || err2}`);
          // Fallback 2: ruta alternativa sin el prefijo /prerequisitos
          try {
            if (verbose) console.debug(`${tag} Trying alt2: GET /clientes/${clienteId}/match?rut=${rut}`);
            const alt2: AxiosResponse<ApiResponse<any>> = await this.api.get(`/clientes/${clienteId}/match`, { params: { rut } });
            if (verbose) console.debug(`${tag} alt2 succeeded for clienteId=${clienteId} rut=${rut}`);
            return alt2.data;
          } catch (err3: any) {
            if (verbose) console.debug(`${tag} alt2 failed: ${err3?.response?.status || err3?.message || err3}`);
            // Fallback 3: si el backend no soporta match endpoints, realizar el cálculo en el cliente
            try {
              if (verbose) console.debug(`${tag} Using local fallback for clienteId=${clienteId} rut=${rut}`);

              // Obtener los prerrequisitos del cliente
              const requisitosResp = await this.getClientePrerequisitos(clienteId);
              const requisitos = ((requisitosResp as any)?.data) || (requisitosResp as any) || [];

              // Obtener documentos de la persona
              const docsResp = await this.getDocumentosByPersona(rut);
              const documentos = ((docsResp as any)?.data) || (docsResp as any) || [];

              // Normalizar tipos requeridos y tipos presentes
              const tiposRequeridos: string[] = (Array.isArray(requisitos) ? requisitos : ((requisitos as any)?.requisitos || [])).map((r: any) => (r.tipo_documento || r.tipo || '').toString().toLowerCase()).filter(Boolean);
              const tiposPresentes: string[] = (Array.isArray(documentos) ? documentos : ((documentos as any)?.data || [])).map((d: any) => (d.tipo_documento || d.tipo || '').toString().toLowerCase()).filter(Boolean);

              const faltantes = tiposRequeridos.filter((t: string) => !tiposPresentes.includes(t));

              const result = {
                success: true,
                data: {
                  rut,
                  clienteId,
                  requisitos,
                  documentos,
                  faltantes,
                  cumple: faltantes.length === 0
                }
              } as ApiResponse<any>;

              if (verbose) console.debug(`${tag} Local fallback result for clienteId=${clienteId} rut=${rut}: cumple=${result.data.cumple} faltantes=${result.data.faltantes.length}`);
              return result;
            } catch (localErr: any) {
              if (verbose) console.error(`${tag} Local fallback error:`, localErr);
              // Si falla el fallback local, propagar el error del intento local
              throw localErr || err;
            }
          }
        }
      }
      // Para otros errores (500, timeout, etc.) dejar que se propaguen
      if (verbose) console.debug(`${tag} Propagating non-404 error for clienteId=${clienteId} rut=${rut}`);
      throw err;
    }
  }

  // POST /prerrequisitos/clientes/:clienteId/match  (batch)
  // body: { ruts: string[], requireAll?: boolean, includeGlobal?: boolean }
  // Fallback: if server doesn't support batch, call matchPrerequisitosCliente for each rut
  async matchPrerequisitosClienteBatch(clienteId: number, ruts: string[], options?: { requireAll?: boolean; includeGlobal?: boolean }): Promise<ApiResponse<any[]>> {
    try {
      const payload: any = { ruts };
      if (typeof options?.requireAll !== 'undefined') payload.requireAll = options?.requireAll;
      if (typeof options?.includeGlobal !== 'undefined') payload.includeGlobal = options?.includeGlobal;
      const response: AxiosResponse<ApiResponse<any[]>> = await this.postWithLegacy(`/requisitos/clientes/${clienteId}/match`, `/prerrequisitos/clientes/${clienteId}/match`, payload);
      return response.data;
    } catch (err: any) {
      console.warn('⚠️ matchPrerrequisitosClienteBatch - batch endpoint not available, falling back to per-rut requests', err?.message || err);
      // Fallback: call single-match endpoint per rut sequentially (or in parallel)
      const promises = ruts.map(rut => this.matchPrerequisitosCliente(clienteId, rut).then(res => ({ rut, data: res })).catch(e => ({ rut, error: e })));
      const settled = await Promise.all(promises);
      // Normalize fallback response to ApiResponse-like array
      const data = settled.map((s: any) => {
        if (s.error) return { rut: s.rut, success: false, error: s.error };
        return { rut: s.rut, success: s.data?.success ?? true, data: s.data?.data ?? s.data };
      });
      return { success: true, data } as unknown as ApiResponse<any[]>;
    }
  }

  // ==================== MÉTODOS PARA PROGRAMACIÓN SEMANAL ====================
  // GET /programacion?cartera_id=:id&semana=:fecha
  async getProgramacionPorCartera(carteraId: number, semana?: string, fecha?: string): Promise<ApiResponse<any>> {
    // Usar endpoint optimizado /programacion-optimizada
    const params: any = { cartera_id: carteraId };
    
    // Si se proporciona semana, calcular fecha_inicio y fecha_fin
    if (semana) {
      const fechaInicio = new Date(semana);
      const fechaFin = new Date(semana);
      fechaFin.setDate(fechaFin.getDate() + 6); // Lunes a domingo
      params.fecha_inicio = fechaInicio.toISOString().split('T')[0];
      params.fecha_fin = fechaFin.toISOString().split('T')[0];
    } else if (fecha) {
      // Si se proporciona solo fecha, usar la semana de esa fecha
      const fechaBase = new Date(fecha);
      const diaSemana = fechaBase.getDay();
      const diff = fechaBase.getDate() - diaSemana + (diaSemana === 0 ? -6 : 1);
      fechaBase.setDate(diff);
      const fechaInicio = new Date(fechaBase);
      const fechaFin = new Date(fechaBase);
      fechaFin.setDate(fechaFin.getDate() + 6);
      params.fecha_inicio = fechaInicio.toISOString().split('T')[0];
      params.fecha_fin = fechaFin.toISOString().split('T')[0];
    }
    
    console.log('🔄 Consultando programación (getProgramacionPorCartera):', params);
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-optimizada', { params });
    console.log('✅ Respuesta programación (getProgramacionPorCartera):', response.data);
    
    // Transformar respuesta para mantener compatibilidad con código existente
    // El endpoint optimizado devuelve {success, data: {cartera, periodo, programacion}}
    // Necesitamos aplanar los trabajadores en el formato antiguo
    if (response.data.success && response.data.data && response.data.data.programacion) {
      const programacionArray: any[] = [];
      response.data.data.programacion.forEach((dia: any) => {
        if (Array.isArray(dia.trabajadores)) {
          dia.trabajadores.forEach((trabajador: any) => {
            programacionArray.push({
              ...trabajador,
              fecha_trabajo: dia.fecha,
              dia_semana: dia.dia_semana
            });
          });
        }
      });
      
      return {
        success: true,
        data: {
          programacion: programacionArray,
          cartera: response.data.data.cartera,
          semana: response.data.data.periodo
        }
      };
    }
    
    return response.data;
  }

  // GET /programacion/persona/:rut?semanas=:num
  async getProgramacionPersona(rut: string, semanas: number = 4): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/programacion/persona/${rut}?semanas=${semanas}`);
    return response.data;
  }

  // GET /programacion/verificar - Verificar si existe programación
  async verificarProgramacion(rut: string, carteraId: number, semanaInicio: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      rut,
      cartera_id: carteraId.toString(),
      semana_inicio: semanaInicio
    });
    
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/programacion/verificar?${params}`);
    return response.data;
  }

  // POST /programacion
  async crearProgramacion(programacion: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/programacion', programacion);
    return response.data;
  }

  // PUT /programacion/:id
  async actualizarProgramacion(id: number, updates: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/programacion/${id}`, updates);
    return response.data;
  }

  // DELETE /programacion/:id
  async eliminarProgramacion(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/programacion/${id}`);
    return response.data;
  }

  // GET /programacion/semana/:fecha
  async getProgramacionSemana(fecha: string): Promise<ApiResponse<any>> {
    console.log('🔍 Obteniendo programación de toda la semana para fecha:', fecha);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/programacion/semana/${fecha}`);
      console.log('📊 Respuesta de programación semanal:', response.data);
      return response.data;
    } catch (e: any) {
      const status = e?.response?.status || e?.status;
      if (status === 404) {
        // Endpoint no implementado o semana sin datos - devolver estructura vacía
        console.debug('[api.getProgramacionSemana] 404 - semana sin datos', { fecha });
        return { success: true, data: [], message: 'No hay programación para esta semana' } as any;
      }
      console.warn('[api.getProgramacionSemana] error', { fecha, status });
      throw e;
    }
  }

  // DELETE /programacion/semana/:fecha?cartera_id=...&cliente_id=...
  async eliminarProgramacionSemanaPorFecha(fecha: string, cartera_id?: number | string, cliente_id?: number | string): Promise<ApiResponse<any>> {
    const paramsArr: string[] = [];
    if (cartera_id !== undefined && cartera_id !== null && `${cartera_id}` !== '') paramsArr.push(`cartera_id=${encodeURIComponent(String(cartera_id))}`);
    if (cliente_id !== undefined && cliente_id !== null && `${cliente_id}` !== '') paramsArr.push(`cliente_id=${encodeURIComponent(String(cliente_id))}`);
    const params = paramsArr.length ? `?${paramsArr.join('&')}` : '';
    const url = `/programacion/semana/${encodeURIComponent(fecha)}${params}`;
    try {
      // Suprimir logs de error para este endpoint específico
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(url, {
        validateStatus: (status) => status < 500 // Tratar 404 como respuesta válida
      });
      
      // Si es 404, devolver respuesta exitosa vacía
      if (response.status === 404) {
        return { success: true, deleted: 0, message: 'No hay programación para eliminar' } as any;
      }
      
      return response.data;
    } catch (e: any) {
      const status = e?.response?.status || e?.status;
      const msg: string = e?.response?.data?.message || e?.message || '';
      
      // Cualquier error se trata como no-op silencioso
      console.debug('[api.eliminarProgramacionSemanaPorFecha] error tratado como no-op', { url, status, msg });
      return { success: true, deleted: 0, message: msg || 'No hay programación para eliminar' } as any;
    }
  }

  // POST /programacion/semana - crear o reemplazar programación por semana (bulk)
  async crearProgramacionSemanaBulk(payload: { semana_inicio: string; planificaciones: any[]; cartera_id?: number | string; overwrite?: boolean }): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/programacion/semana', payload);
      return response.data;
    } catch (e: any) {
      const status = e?.response?.status || e?.status;
      
      // Manejar 404 (endpoint no existe) o 400 (formato incorrecto)
      if (status === 404 || status === 400) {
        const errorMsg = e?.response?.data?.message || e?.message || '';
        console.debug(`[api.crearProgramacionSemanaBulk] ${status} - ${errorMsg}, usando fallback`);
        
        // Si no hay planificaciones, simplemente retornar éxito
        if (!payload.planificaciones || payload.planificaciones.length === 0) {
          return { success: true, data: { created: 0 }, message: 'Sin planificaciones para crear' } as any;
        }
        
        // Intentar crear registros individuales como fallback
        try {
          const results = await Promise.allSettled(
            payload.planificaciones.map(p => this.crearProgramacion(p))
          );
          const succeeded = results.filter((r: any) => r.status === 'fulfilled').length;
          return { 
            success: true, 
            data: { created: succeeded, total: payload.planificaciones.length },
            message: `Creados ${succeeded} de ${payload.planificaciones.length} registros (método legacy)`
          } as any;
        } catch (fallbackErr) {
          console.debug('[api.crearProgramacionSemanaBulk] fallback también falló', fallbackErr);
          return { success: false, message: 'No se pudo crear la programación' } as any;
        }
      }
      
      console.error('[api.crearProgramacionSemanaBulk] error', e);
      throw e;
    }
  }

  // ==================== MÉTODOS PARA DOCUMENTOS VENCIDOS ====================
  // GET /documentos/vencidos
  async getDocumentosVencidos(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/documentos/vencidos');
    return response.data;
  }

  // GET /documentos/vencer
  async getDocumentosPorVencer(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/documentos/vencer');
    return response.data;
  }

  // PUT /documentos/:id - Actualizar documento con fechas de validez
  async actualizarDocumento(id: number, datos: {
    fecha_emision?: string;
    fecha_vencimiento?: string;
    dias_validez?: number;
    estado_documento?: string;
    institucion_emisora?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/documentos/${id}`, datos);
    return response.data;
  }

  // ==================== MÉTODOS PARA MÍNIMO PERSONAL ====================
  
  // GET /api/servicios/minimo-personal
  async getMinimoPersonal(params?: { 
    limit?: number; 
    offset?: number; 
    search?: string;
    servicio_id?: number;
    cartera_id?: number;
    cliente_id?: number;
    nodo_id?: number;
  }): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/servicios/minimo-personal', { params });
    return response.data;
  }

  // POST /api/servicios/minimo-personal
  async createMinimoPersonal(data: {
    servicio_id: number;
    cartera_id: number;
    cliente_id?: number;
    nodo_id?: number;
    minimo_personal: number;
    descripcion?: string;
    activo?: boolean;
    personas_seleccionadas?: string[];
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/minimo-personal', data);
    return response.data;
  }

  // GET /api/servicios/minimo-personal/:id
  async getMinimoPersonalById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/minimo-personal/${id}`);
    return response.data;
  }

  // PUT /api/servicios/minimo-personal/:id
  async updateMinimoPersonal(id: number, data: {
    servicio_id?: number;
    cartera_id?: number;
    cliente_id?: number;
    nodo_id?: number;
    minimo_personal?: number;
    minimo_base?: number;
    minimo_temporada_alta?: number;
    minimo_temporada_baja?: number;
    descripcion?: string;
    activo?: boolean;
    personas_seleccionadas?: string[];
  }): Promise<ApiResponse<any>> {
    // If an explicit services URL is provided via env, use it (useful for ports like :3001 and different path)
    // Expect REACT_APP_SERVICIOS_URL to be something like 'http://localhost:3001/servicios' (no trailing slash recommended)
    // Read env var safely in browser: `process` may be undefined at runtime
    const serviciosBase = (typeof process !== 'undefined' && (process.env as any).REACT_APP_SERVICIOS_URL) || null;
    if (serviciosBase) {
      const base = String(serviciosBase).replace(/\/$/, '');
      const fullUrl = `${base}/minimo-personal/${id}`;
      // Use the configured axios instance so request interceptors (Authorization) are applied.
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(fullUrl, data as any);
      return response.data;
    }

    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/minimo-personal/${id}`, data);
    return response.data;
  }

  // DELETE /api/servicios/minimo-personal/:id
  async deleteMinimoPersonal(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicios/minimo-personal/${id}`);
    return response.data;
  }

  // GET /api/servicios/minimo-personal/:id/calcular
  async calcularMinimoPersonal(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/minimo-personal/${id}/calcular`);
    return response.data;
  }

  // ==================== MÉTODOS PARA ACUERDOS ====================
  
  // GET /api/servicios/acuerdos
  async getAcuerdos(params?: { 
    limit?: number; 
    offset?: number; 
    search?: string;
    tipo_acuerdo?: string;
    estado?: string;
  }): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/servicios/acuerdos', { params });
    return response.data;
  }

  // POST /api/servicios/acuerdos
  async createAcuerdo(data: {
    nombre: string;
    descripcion?: string;
    tipo_acuerdo: 'servicio' | 'personal' | 'cliente' | 'general';
    fecha_inicio: string;
    fecha_fin: string;
    condiciones?: string;
    observaciones?: string;
    estado?: 'activo' | 'inactivo' | 'vencido' | 'pendiente';
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicios/acuerdos', data);
    return response.data;
  }

  // GET /api/servicios/acuerdos/:id
  async getAcuerdoById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicios/acuerdos/${id}`);
    return response.data;
  }

  // PUT /api/servicios/acuerdos/:id
  async updateAcuerdo(id: number, data: {
    nombre?: string;
    descripcion?: string;
    tipo_acuerdo?: 'servicio' | 'personal' | 'cliente' | 'general';
    fecha_inicio?: string;
    fecha_fin?: string;
    condiciones?: string;
    observaciones?: string;
    estado?: 'activo' | 'inactivo' | 'vencido' | 'pendiente';
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicios/acuerdos/${id}`, data);
    return response.data;
  }

  // DELETE /api/servicios/acuerdos/:id
  async deleteAcuerdo(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicios/acuerdos/${id}`);
    return response.data;
  }

  // GET /api/servicios/acuerdos/vencer
  async getAcuerdosVencer(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/servicios/acuerdos/vencer');
    return response.data;
  }

  // POST /api/servicios/acuerdos/:id/activar
  async activarAcuerdo(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/servicios/acuerdos/${id}/activar`);
    return response.data;
  }

  // POST /api/servicios/acuerdos/:id/desactivar
  async desactivarAcuerdo(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/servicios/acuerdos/${id}/desactivar`);
    return response.data;
  }

  // ==================== MÉTODOS PARA PROGRAMACIÓN OPTIMIZADA ====================
  
  // GET /api/programacion-optimizada
  async getProgramacionOptimizada(params: {
    cartera_id: number;
    fecha_inicio?: string;
    fecha_fin?: string;
    semana?: string;
    fecha?: string;
  }): Promise<ApiResponse<any>> {
    console.log('🔄 Consultando programación optimizada:', {
      endpoint: '/programacion-optimizada',
      params,
      baseURL: this.api.defaults.baseURL
    });
    
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-optimizada', { params });
      console.log('✅ Respuesta de programación optimizada:', response.data);
      
      // Si no hay datos de programación, inicializar estructura
      if (!response.data?.data?.programacion) {
        console.log('⚠️ No hay datos de programación, inicializando estructura');
        return {
          success: true,
          data: {
            cartera: {
              id: params.cartera_id,
              nombre: ''
            },
            programacion: [],
            filters: params,
            timestamp: new Date().toISOString()
          }
        };
      }
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Error consultando programación optimizada:', error);
      console.error('❌ Response:', error.response?.data);
      throw error;
    }
  }

  // POST /api/programacion-optimizada
  async crearProgramacionOptimizada(payload: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/programacion-optimizada', payload);
    return response.data;
  }

  // DELETE /api/programacion-optimizada/:id
  async deleteProgramacionOptimizada(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/programacion-optimizada/${id}`);
    return response.data;
  }

  // GET /api/programacion-optimizada/calendario
  async getCalendarioOptimizado(params: {
    cartera_id: number;
    mes?: number;
    año?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-optimizada/calendario', { params });
    return response.data;
  }

  // GET /api/programacion-optimizada/carteras-con-datos
  async getCarterasConDatos(params: { inicio: string; fin: string; }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-optimizada/carteras-con-datos', { params });
    return response.data;
  }

  // GET /api/programacion-optimizada/:id
  async getProgramacionOptimizadaById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/programacion-optimizada/${id}`);
    return response.data;
  }

  // PUT /api/programacion-optimizada/:id
  async actualizarProgramacionOptimizada(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/programacion-optimizada/${id}`, data);
    return response.data;
  }

  // DELETE /api/programacion-optimizada/:id
  async eliminarProgramacionOptimizada(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/programacion-optimizada/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA PROGRAMACIÓN SEMANAL SIMPLIFICADA ====================
  
  // GET /api/programacion-semanal
  async getProgramacionSemanal(params: {
    cartera_id: number;
    fecha_inicio: string;
    fecha_fin: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-semanal', { params });
    return response.data;
  }

  // POST /api/dashboard/top-acreditable
  async getTopAcreditable(payload: {
    semana_inicio: string;
    cliente_id?: number;
    cartera_id?: number;
    priorizar_planificacion?: boolean;
    top_n?: number;
    max_clientes?: number;
    max_personas_por_cliente?: number;
  }): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post('/dashboard/top-acreditable', payload);
    return response.data;
  }

  // POST /api/programacion-semanal
  async crearProgramacionSemanal(data: {
    rut: string;
    cartera_id: number;
    cliente_id?: number;
    nodo_id?: number;
    fecha_trabajo: string;
    horas_estimadas?: number;
    observaciones?: string;
    estado?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/programacion-semanal', data);
    return response.data;
  }

  // DELETE /api/programacion-semanal/:id
  async eliminarProgramacionSemanal(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/programacion-semanal/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA PERSONAL POR CLIENTE ====================
  
  // GET /api/personal-por-cliente
  async getPersonalPorCliente(params?: {
    cliente_id?: number;
    cartera_id?: number;
    fecha_inicio?: string;
    fecha_fin?: string;
    activo?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/personal-por-cliente', { params });
    return response.data;
  }

  // GET /api/personal-por-cliente/:cliente_id
  async getPersonalPorClienteId(clienteId: number, params?: {
    fecha_inicio?: string;
    fecha_fin?: string;
    activo?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/personal-por-cliente/${clienteId}`, { params });
    return response.data;
  }

  // GET /api/personal-por-cliente/batch?cliente_ids=1,2,3 — personal de varios clientes en
  // una sola llamada (reemplaza el loop de getPersonalPorClienteId por cliente).
  // Devuelve { [clienteId]: { cliente_id, cliente_nombre, ..., personal: [...] } }.
  async getPersonalPorClienteBatch(clienteIds: (number | string)[], params?: {
    fecha_inicio?: string;
    fecha_fin?: string;
    activo?: boolean;
  }): Promise<Record<string, any>> {
    if (clienteIds.length === 0) return {};
    const response: AxiosResponse<ApiResponse<Record<string, any>>> = await this.api.get(
      '/personal-por-cliente/batch',
      { params: { ...params, cliente_ids: clienteIds.join(',') } }
    );
    return response.data?.data ?? {};
  }

  // GET /api/personal-por-cliente/resumen
  async getResumenPersonalPorCliente(params?: {
    cartera_id?: number;
    fecha_inicio?: string;
    fecha_fin?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/personal-por-cliente/resumen', { params });
    return response.data;
  }

  // Registrar un documento que existe en almacenamiento externo (p.e. Google Drive)
  // POST /api/documentos/registrar-existente
  async registerExistingDocument(payload: any): Promise<ApiResponse<any>> {
    try {
      // Extraer y mapear los campos del objeto file al formato que espera el backend
      const file = payload.file || {};
      const mappedPayload = {
        rut_persona: payload.rut_persona || payload.rut || payload.rut_person || null,
        nombre_documento: payload.nombre_documento || payload.nombre || null,
        // Si no se especifica, usar 'otro' para cumplir con backend que suele requerir tipo
        tipo_documento: payload.tipo_documento || 'otro',
        tipo_slug: payload.tipo_slug || undefined,
        // Campos extraídos del objeto file (variantes posibles)
        nombre_archivo: file.nombre_archivo || file.nombre_original || file.name || file.title || file.filename || null,
        nombre_original: file.nombre_original || file.name || file.title || null,
        drive_file_id: file.drive_file_id || file.id || file.fileId || file.driveId || null,
  ruta_local: file.ruta_local || file.ruta || file.path || file.drive_path || file.webViewLink || (file.drive_file_id ? `drive://${file.drive_file_id}` : null),
        tipo_mime: file.tipo_mime || file.mimeType || file.type || null,
        tamano_bytes: file.tamaño_bytes || file.size || file.bytes || null,
        // Metadatos opcionales
        descripcion: payload.descripcion || null,
        fecha_emision: payload.fecha_emision || null,
        fecha_vencimiento: payload.fecha_vencimiento || null,
        dias_validez: payload.dias_validez || null,
        estado_documento: payload.estado_documento || null,
        institucion_emisora: payload.institucion_emisora || null,
        cliente_id: payload.cliente_id ?? null,
        prerrequisitos: Array.isArray(payload.prerrequisitos) ? payload.prerrequisitos : undefined,
      };

      // Normalizar tipo_documento a valores permitidos por el backend
      const normalizeTipo = (t: any) => {
        if (!t) return null;
        const s = String(t).toLowerCase().trim();
        const map: Record<string, string> = {
          'epp': 'certificado_seguridad',
          'eps': 'certificado_seguridad',
          'seguridad': 'certificado_seguridad',
          'curso': 'certificado_curso',
          'certificacion': 'certificado_curso',
          'certificación': 'certificado_curso',
          'diploma': 'diploma',
          'contrato': 'certificado_laboral',
          'laboral': 'certificado_laboral',
          'licencia': 'licencia_conducir',
          'licencia_conducir': 'licencia_conducir',
          'medico': 'certificado_medico',
          'certificado_medico': 'certificado_medico',
          'vencimiento': 'certificado_vencimiento',
          'certificado_vencimiento': 'certificado_vencimiento',
          'otro': 'otro'
        };
        return map[s] || (['certificado_curso','diploma','certificado_laboral','certificado_medico','licencia_conducir','certificado_seguridad','certificado_vencimiento','otro'].includes(s) ? s : null);
      };

      // Validar campos requeridos antes de enviar para evitar 400 genéricos
      const missing: string[] = [];
      if (!mappedPayload.rut_persona) missing.push('rut_persona');
      if (!mappedPayload.nombre_archivo) missing.push('nombre_archivo');
      if (!mappedPayload.ruta_local) missing.push('ruta_local');
      if (!mappedPayload.nombre_documento) {
        // fallback: si no hay nombre_documento, usar nombre_archivo
        mappedPayload.nombre_documento = mappedPayload.nombre_archivo || null;
      }
      // Normalize tipo_documento and ensure it matches backend allowed values
      const normalizedTipo = normalizeTipo(mappedPayload.tipo_documento);
      mappedPayload.tipo_documento = normalizedTipo || 'otro';

      if (missing.length > 0) {
        const err = new Error(`Faltan campos requeridos: ${missing.join(', ')}`);
        // adjuntar info para debugging
        // @ts-ignore
        err.payload = mappedPayload;
        throw err;
      }

      // Log claro en stringified form para poder ver en consola incluso si el object tiene referencias
      try {
        console.log('📤 Payload mapeado para registrar-existente:', JSON.stringify(mappedPayload));
      } catch (e) {
        console.log('📤 Payload mapeado para registrar-existente (raw):', mappedPayload);
      }

      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/documentos/registrar-existente', mappedPayload);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error al registrar documento existente:', error?.message || error);
      try { console.error('❌ Payload original:', JSON.stringify(payload)); } catch (e) { console.error('❌ Payload original (raw):', payload); }
      try { console.error('❌ Respuesta del servidor:', JSON.stringify(error.response?.data)); } catch (e) { console.error('❌ Respuesta del servidor (raw):', error.response?.data); }
      throw error;
    }
  }

  // ==================== MÉTODOS PARA COMPATIBILIDAD ====================

  // GET /api/programacion-compatibilidad
  async getProgramacionCompatibilidad(params: {
    cartera_id: number;
    semana: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/programacion-compatibilidad', { params });
    return response.data;
  }

  // POST /api/programacion-compatibilidad
  async crearProgramacionCompatibilidad(data: {
    rut: string;
    cartera_id: number;
    cliente_id?: number;
    nodo_id?: number;
    semana_inicio: string;
    lunes?: boolean;
    martes?: boolean;
    miercoles?: boolean;
    jueves?: boolean;
    viernes?: boolean;
    sabado?: boolean;
    domingo?: boolean;
    horas_estimadas?: number;
    observaciones?: string;
    estado?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/programacion-compatibilidad', data);
    return response.data;
  }

  // ==================== MÉTODOS PARA CARPETAS PERSONAL ====================
  
  // GET /api/carpetas-personal
  async getCarpetasPersonal(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/carpetas-personal');
    return response.data;
  }

  // ==================== MÉTODOS PARA AUDITORÍA ====================

  // GET /api/auditoria/dashboard - Obtener dashboard de actividad
  async getAuditoriaDashboard(params?: {
    limit?: number;
    es_critico?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auditoria/dashboard', { params });
    return response.data;
  }

  // GET /api/auditoria/historial/:tabla/:id - Ver historial de un registro específico
  async getAuditoriaHistorial(tabla: string, id: string | number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditoria/historial/${tabla}/${id}`);
    return response.data;
  }

  // GET /api/auditoria/estadisticas - Obtener estadísticas de los últimos 30 días
  async getAuditoriaEstadisticas(params?: {
    periodo?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auditoria/estadisticas', { params });
    return response.data;
  }

  // GET /api/auditoria/notificaciones - Ver notificaciones no leídas
  async getAuditoriaNotificaciones(params?: {
    leida?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auditoria/notificaciones', { params });
    return response.data;
  }

  // ==================== NOTIFICACIONES POR USUARIO/ROL ====================
  // /api/notificaciones — persistidas en BD, dirigidas por rol o usuario (routes/notificaciones.js
  // en el backend). NO confundir con getAuditoriaNotificaciones de arriba, que lee la tabla vieja
  // sistema.notificaciones (casi vacía, solo alimenta el dashboard de auditoría).

  // GET /api/notificaciones - bandeja del usuario autenticado
  async getNotificaciones(params?: {
    leida?: boolean;
    tipo?: string;
    prioridad?: 'alta' | 'media' | 'baja';
    descartada?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/notificaciones', { params });
    return response.data;
  }

  // GET /api/notificaciones/no-leidas/count - contador liviano para la campana
  async getNotificacionesNoLeidasCount(): Promise<ApiResponse<{ total_no_leidas: number }>> {
    const response: AxiosResponse<ApiResponse<{ total_no_leidas: number }>> = await this.api.get('/notificaciones/no-leidas/count');
    return response.data;
  }

  // PUT /api/notificaciones/:id/leida
  async marcarNotificacionLeida(id: number | string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/notificaciones/${id}/leida`);
    return response.data;
  }

  // PUT /api/notificaciones/marcar-todas-leidas
  async marcarTodasNotificacionesLeidas(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/notificaciones/marcar-todas-leidas');
    return response.data;
  }

  // DELETE /api/notificaciones/:id - enviar a la papelera (solo la copia del usuario actual)
  async eliminarNotificacion(id: number | string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/notificaciones/${id}`);
    return response.data;
  }

  // PUT /api/notificaciones/:id/restaurar - sacar de la papelera
  async restaurarNotificacion(id: number | string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/notificaciones/${id}/restaurar`);
    return response.data;
  }

  // POST /api/notificaciones - creación manual, dirigida por rol y/o usuario (requiere
  // permiso notifications.manage)
  async crearNotificacionManual(data: {
    tipo?: string;
    titulo: string;
    mensaje: string;
    prioridad?: 'alta' | 'media' | 'baja';
    entidad_tipo?: string;
    entidad_id?: string | number;
    metadata?: Record<string, any>;
    expira_en?: string;
    destinatarios: { roles?: string[]; usuarioIds?: number[] };
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/notificaciones', data);
    return response.data;
  }

  // GET /api/users - listado de usuarios (requiere permiso users.manage). Usado por el
  // panel de notificaciones manuales para elegir destinatarios puntuales. Nota: a
  // diferencia del resto de api.ts, este endpoint no sigue la convención {success, data} —
  // responde {message, users, pagination} directamente (ver routes/users.js).
  async getUsuarios(params?: { search?: string; limit?: number; page?: number }): Promise<{ message: string; users: any[]; pagination: any }> {
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  // POST /api/users - crea un usuario (permiso users.manage). Igual que el GET, responde
  // {message, user} sin el sobre {success, data}. Un `rol` fuera del CHECK legado
  // (admin/supervisor/usuario/operador) se guarda vía zc_temp.usuario_rol_override y el
  // backend responde 400 si no existe en sistema.roles.
  async createUsuario(data: { email: string; password: string; nombre: string; apellido: string; rol?: string }): Promise<{ message: string; user: any }> {
    const response = await this.api.post('/users', data);
    return response.data;
  }

  // ── Roles configurables (zc_backend/routes/roles.js, permiso users.manage) ──────────
  // El catálogo (qué pestañas y acciones existen, cómo se agrupan y qué acción exige cada
  // pestaña) lo define el backend en config/catalogoPermisos.js.
  // ── Responsables de documentos (zc_backend/routes/responsabilidades.js) ─────────────
  // Quién responde por cada documento de un cliente o mandante, sus pendientes en vivo y la
  // supervisión del administrador (accreditation.manage). Ver docs/endpoints/responsabilidades.md.
  async getMisResponsabilidadesEstado(): Promise<any> {
    const response = await this.api.get('/responsabilidades/mias/estado');
    return response.data.data;
  }

  async getResponsablesSupervision(usuarioId?: number): Promise<any> {
    const response = await this.api.get('/responsabilidades/supervision', { params: usuarioId ? { usuario_id: usuarioId } : undefined });
    return response.data.data;
  }

  async getResponsablesClientes(): Promise<{ clientes: any[]; empresas: any[] }> {
    const response = await this.api.get('/responsabilidades/clientes');
    return response.data.data;
  }

  async getResponsablesRequisitos(params: { cliente_id?: number; empresa_id?: number }): Promise<any[]> {
    const response = await this.api.get('/responsabilidades/requisitos', { params });
    return response.data.data;
  }

  async getUsuariosAsignables(): Promise<any[]> {
    const response = await this.api.get('/responsabilidades/usuarios-asignables');
    return response.data.data;
  }

  async asignarResponsable(data: { usuario_id: number; prereq_id?: number; empresa_prereq_id?: number }): Promise<any> {
    const response = await this.api.post('/responsabilidades', data);
    return response.data;
  }

  async quitarResponsable(id: number): Promise<any> {
    const response = await this.api.delete(`/responsabilidades/${id}`);
    return response.data;
  }

  // Actividad de avisos: solicitudes de carga del administrador y seguimiento de cada aviso.
  async enviarSolicitudCarga(data: { usuario_id: number; items?: { responsabilidad_id: any; rut: string | null }[]; mensaje?: string; plazo?: string | null }): Promise<any> {
    const response = await this.api.post('/responsabilidades/solicitudes', data);
    return response.data.data;
  }

  async getActividadAvisos(params?: { usuario_id?: number; origen?: string; limit?: number }): Promise<any[]> {
    const response = await this.api.get('/responsabilidades/actividad', { params });
    return response.data.data;
  }

  async getActividadEncargados(dias = 30): Promise<any> {
    const response = await this.api.get('/responsabilidades/actividad/encargados', { params: { dias } });
    return response.data.data;
  }

  async getDetalleAviso(id: number): Promise<any> {
    const response = await this.api.get(`/responsabilidades/actividad/${id}`);
    return response.data.data;
  }

  async getRolesCatalogo(): Promise<RolesCatalogo> {
    const response = await this.api.get('/roles/catalogo');
    return response.data.data;
  }

  async getRoles(): Promise<RolConfigurable[]> {
    const response = await this.api.get('/roles');
    return response.data.data;
  }

  async createRol(data: { nombre: string; descripcion: string; permisos: string[] }): Promise<RolConfigurable> {
    const response = await this.api.post('/roles', data);
    return response.data.data;
  }

  // `permisos` REEMPLAZA el set completo del rol.
  async updateRol(key: string, data: { nombre?: string; descripcion?: string; permisos?: string[]; activo?: boolean }): Promise<RolConfigurable> {
    const response = await this.api.put(`/roles/${encodeURIComponent(key)}`, data);
    return response.data.data;
  }

  // PUT /api/users/:id - actualiza un usuario. `activo` y `rol` solo los aplica el backend
  // si quien llama tiene users.manage. Un usuario inactivo no puede iniciar sesión y sus
  // tokens vigentes dejan de valer (authenticateToken filtra por activo = true).
  async updateUsuario(id: number, data: { email?: string; nombre?: string; apellido?: string; rol?: string; activo?: boolean }): Promise<{ message: string; user: any }> {
    const response = await this.api.put(`/users/${id}`, data);
    return response.data;
  }

  // Consulta paginada para listados interactivos. A diferencia de getPersonal,
  // no recorre todas las páginas: la búsqueda y el total se resuelven en el
  // backend para no saturarlo mientras el usuario escribe.
  async getPersonalPagina(page = 1, limit = 10, search = '', filters: Record<string, any> = {}): Promise<PaginatedResponse<Personal>> {
    const params: Record<string, string | number | boolean> = {
      limit,
      offset: Math.max(0, page - 1) * limit,
    };
    if (search.trim()) params.search = search.trim();
    for (const key of ['estado_id', 'estado', 'cargo', 'es_nuevo', 'fields']) {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    }
    const response: AxiosResponse<PaginatedResponse<Personal>> = await this.api.get('/personal-disponible', { params });
    return response.data;
  }

  async resetearContrasenaUsuario(id: number, newPassword: string): Promise<{ message: string; user: any }> {
    const response = await this.api.post(`/users/${id}/reset-password`, { newPassword });
    return response.data;
  }

  // POST /api/email/send — envía un correo con adjuntos opcionales.
  // El backend (routes/email.js) solo exige estar autenticado, así que un rol
  // `planificacion` puede usarlo. Se manda como multipart porque el endpoint acepta
  // los archivos por `adjuntos[]` vía multer (hasta 5 archivos, 25MB por envío).
  async enviarEmail(payload: {
    destinatarios: string[];
    /** Copia (CC). */
    cc?: string[];
    asunto: string;
    cuerpo: string;
    adjuntos?: Array<{ nombre: string; blob: Blob }>;
  }): Promise<ApiResponse<{ messageId: string; destinatarios: string[]; adjuntos: number }>> {
    const formData = new FormData();
    formData.append('destinatarios', payload.destinatarios.join(','));
    if (payload.cc?.length) formData.append('cc', payload.cc.join(','));
    formData.append('asunto', payload.asunto);
    formData.append('cuerpo', payload.cuerpo);
    for (const adj of payload.adjuntos || []) {
      formData.append('adjuntos', adj.blob, adj.nombre);
    }

    // Instancia aparte, igual que uploadDocumento: `this.api` tiene
    // 'Content-Type: application/json' por defecto y axios v1 serializa un FormData
    // a JSON cuando ese header está presente — el adjunto se perdería en silencio.
    const uploadApi = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
      withCredentials: false,
    });

    const token = typeof window !== 'undefined' ? getTokenIfValid() : null;
    if (!token) {
      clearSession();
      try { window.location.href = getLoginPath(); } catch (e) {}
      throw new Error('Session expired');
    }

    const response: AxiosResponse<ApiResponse<any>> = await uploadApi.post('/email/send', formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  // GET /api/carpetas-personal/:id
  async getCarpetaPersonalById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/carpetas-personal/${id}`);
    return response.data;
  }

  // POST /api/carpetas-personal
  async crearCarpetaPersonal(data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/carpetas-personal', data);
    return response.data;
  }

  // PUT /api/carpetas-personal/:id
  async actualizarCarpetaPersonal(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/carpetas-personal/${id}`, data);
    return response.data;
  }

  // DELETE /api/carpetas-personal/:id
  async eliminarCarpetaPersonal(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/carpetas-personal/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA AUDITORÍA ====================
  
  // GET /api/auditoria
  async getAuditoria(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/auditoria');
    return response.data;
  }

  // GET /api/auditoria/:id
  async getAuditoriaById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditoria/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA SERVICIO ====================
  
  // GET /api/servicio
  async getServicios(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/servicio');
    return response.data;
  }

  // GET /api/servicio/:id
  async getServicioById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/servicio/${id}`);
    return response.data;
  }

  // POST /api/servicio
  async crearServicio(data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/servicio', data);
    return response.data;
  }

  // PUT /api/servicio/:id
  async actualizarServicio(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/servicio/${id}`, data);
    return response.data;
  }

  // DELETE /api/servicio/:id
  async eliminarServicio(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/servicio/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA REQUISITOS ====================

  // GET /api/prerrequisitos/clientes/:clienteId  (fallbacks)
  async getPrerrequisitosByCliente(clienteId: number): Promise<ApiResponse<any[]>> {
    for (const url of [
      `/prerrequisitos/cliente/${clienteId}`,
      `/prerrequisitos/clientes/${clienteId}`,
      `/requisitos/cliente/${clienteId}`,
      `/requisitos/clientes/${clienteId}`,
    ]) {
      try {
        const resp: AxiosResponse<any> = await this.api.get(url);
        const raw = resp.data;
        const data = Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw?.data?.data) ? raw.data.data
          : Array.isArray(raw) ? raw
          : null;
        if (data !== null) return { ...raw, data } as ApiResponse<any[]>;
      } catch (e: any) {
        if (e?.response?.status !== 404) throw e;
      }
    }
    return { success: true, data: [] } as unknown as ApiResponse<any[]>;
  }

  // PUT /api/prerrequisitos/:id/responsable — asigna o limpia el área responsable de un requisito.
  // area = '' o null → limpia. Valores válidos: 'RRHH' | 'Prevención' | 'N/A'.
  // Devuelve { prereq_id, area: PrereqArea | null }.
  async setPrerrequisitoResponsable(
    prereqId: number | string,
    area: PrereqArea | null
  ): Promise<ApiResponse<{ prereq_id: number; area: PrereqArea | null }>> {
    // El backend monta el endpoint en /api/prerrequisitos y su alias /api/prerequisitos;
    // no hay variante /requisitos, así que llamamos directo (sin putWithLegacy) para que
    // los errores de validación —requisito inexistente o área inválida— se propaguen tal cual.
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/prerrequisitos/${prereqId}/responsable`,
      { area: area ?? '' }
    );
    return response.data;
  }

  // GET /api/requisitos/clientes/:clienteId/parciales
  async getPartialPrerequisitosCliente(clienteId: number, params: { includeGlobal?: boolean; limit?: number; offset?: number }): Promise<ApiResponse<any[]>> {
    const label = `GET /requisitos/clientes/${clienteId}/parciales`;
    cloudRunLog('Request start', { url: label, params });
    cloudRunTimeStart(label);
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/requisitos/clientes/${clienteId}/parciales`, { params });
    cloudRunTimeEnd(label);
    cloudRunLog('Request end', { url: label, status: (response as any)?.status, count: Array.isArray(response?.data?.data) ? response.data.data.length : (Array.isArray(response?.data) ? (response.data as any).length : undefined) });
    cloudRunSummarize(response);
    return response.data;
  }

  // POST /api/requisitos/clientes/:clienteId/cobertura-masiva
  // Endpoint BFF: recibe un array de RUTs y devuelve cobertura para todos en una sola llamada.
  async getPrerequisitosCoberturaMasiva(clienteId: number, ruts: string[]): Promise<Record<string, { prereq_covered_count: number; prereq_total: number }>> {
    const response: AxiosResponse<any> = await this.api.post(
      `/requisitos/clientes/${encodeURIComponent(String(clienteId))}/cobertura-masiva`,
      { ruts }
    );
    const results: Record<string, { prereq_covered_count: number; prereq_total: number }> = response.data?.data ?? {};
    return results;
  }

  // ==================== PERSONAL ROLES ====================

  // GET /api/personal-roles — lista completa, opcionalmente filtrada.
  // Cada fila trae además `estado_ingreso` (pendiente | habilitado | de_baja | null), `estado_fecha`,
  // `estado_por`, `estado_motivo` y `alerta_baja` (backend migrations/067). Por defecto el backend
  // oculta a los de baja; `incluir_baja: true` los trae.
  async getPersonalRoles(params?: { cliente_id?: number; rol?: string; estado?: string; incluir_baja?: boolean }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/personal-roles', { params });
    return response.data;
  }

  // GET /api/personal-roles/:clienteId — personal de un cliente agrupado por rol con conteo de documentos
  async getPersonalRolesByCliente(clienteId: number, params?: { estado?: string; incluir_baja?: boolean }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/personal-roles/${clienteId}`, { params });
    return response.data;
  }

  // PATCH /api/personal-roles/clientes/:clienteId/estado — Pendiente / Habilitado / De baja en el cliente.
  // `motivo` es obligatorio para 'de_baja'.
  async cambiarEstadoIngreso(
    clienteId: number,
    body: { rut: string; estado: EstadoIngreso; motivo?: string }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(
      `/personal-roles/clientes/${clienteId}/estado`,
      body
    );
    return response.data;
  }

  // POST /api/personal-roles/clientes/:clienteId/estado-masivo — planilla pegada de la plataforma
  // del cliente. Con dry_run (default) solo devuelve el plan; sin él, lo aplica en una transacción.
  async estadoIngresoMasivo(
    clienteId: number,
    body: { texto: string; quitar_ausentes?: boolean; dry_run?: boolean }
  ): Promise<{ success: boolean; dry_run: boolean; plan: PlanEstadoIngreso; aplicado?: { agregados: number; actualizados: number; dados_de_baja: number }; message?: string }> {
    const response = await this.api.post(`/personal-roles/clientes/${clienteId}/estado-masivo`, body);
    return response.data;
  }

  // GET /api/personal-roles/clientes/:clienteId/estado-historial?rut=
  async getEstadoIngresoHistorial(clienteId: number, rut?: string): Promise<{ success: boolean; data: HistorialEstadoIngreso[] }> {
    const response = await this.api.get(`/personal-roles/clientes/${clienteId}/estado-historial`, {
      params: rut ? { rut } : undefined,
    });
    return response.data;
  }

  // POST /api/personal-roles/clientes/:clienteId/agregar — Agrega un personal al roster del cliente
  async agregarPersonalAlCliente(
    clienteId: number,
    body: {
      rut: string;
      rol: string;
      tipo?: 'base' | 'extra';
      // Clasificación del mandante para esta persona en esta faena (columna `vinculacion`
      // de personal_clientes_roles). Complementa a `tipo`, no lo reemplaza.
      vinculacion?: 'base_fijo' | 'base_exclusivo' | 'acreditado_no_exclusivo' | 'acreditado_apoyo';
      notas?: string;
    }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/personal-roles/clientes/${clienteId}/agregar`,
      body
    );
    return response.data;
  }

  // POST /api/personal-roles/clientes/:clienteId/quitar — Quita personal del roster del cliente
  async quitarPersonalDelCliente(
    clienteId: number,
    body: { rut?: string; rol?: string; id?: number }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/personal-roles/clientes/${clienteId}/quitar`,
      body
    );
    return response.data;
  }

  // ==================== TIPOS DE DOCUMENTO ====================

  // GET /api/tipos-documento — catálogo para el selector de subida, con lo que acredita cada tipo
  async getTiposDocumento(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/tipos-documento');
    return response.data;
  }

  // GET /api/tipos-documento/sugerir?nombre= — tipo sugerido para un nombre de documento
  async sugerirTipoDocumento(nombre: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/tipos-documento/sugerir', { params: { nombre } });
    return response.data;
  }

  // GET /api/tipos-documento/impacto?documento_id=&tipo_slug= — qué requisitos/clientes gana y
  // pierde un documento si se le fuerza este tipo. Se llama ANTES de crearMatchManual cuando el
  // documento ya tiene otro tipo, para mostrar el impacto y pedir confirmación.
  async getImpactoTipoDocumento(documentoId: number | string, tipoSlug: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/tipos-documento/impacto', {
      params: { documento_id: documentoId, tipo_slug: tipoSlug },
    });
    return response.data;
  }

  // PUT /api/tipos-documento/requisito/:id — asigna (o quita, con tipoSlug null/undefined) el
  // tipo de un requisito de cliente. Necesario antes de poder asociarle un documento: sin tipo,
  // crearMatchManual devuelve 422 requisito_sin_tipo.
  async asignarTipoRequisito(prereqId: number | string, tipoSlug: string | null): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/tipos-documento/requisito/${prereqId}`, { tipo_slug: tipoSlug });
    return response.data;
  }

  // ==================== MATCH MANUAL ====================

  // GET /api/match-manual?cliente_id=X&rut=Y — Ver asociaciones del trabajador para un cliente
  async getMatchManual(clienteId: number, rut: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/match-manual', {
      params: { cliente_id: clienteId, rut }
    });
    return response.data;
  }

  // GET /api/match-manual/candidatos?cliente_id=X&rut=Y[&prereq_id=Z] — Documentos disponibles
  // para asignar. Con prereq_id cada candidato trae además sus tipos, si ya cubre el requisito
  // por tipo (`ya_cubre_tipo`) y si tiene otro tipo que se le forzaría (`conflicto_tipo`); la
  // respuesta trae `requisito_sin_tipo` para ofrecer tipificarlo antes de asociar nada.
  async getMatchManualCandidatos(clienteId: number, rut: string, prereqId?: number | string): Promise<any> {
    console.log('🌐 API - getMatchManualCandidatos:', { clienteId, rut, prereqId });
    try {
      const response: AxiosResponse<any> = await this.api.get('/match-manual/candidatos', {
        params: { cliente_id: clienteId, rut, ...(prereqId != null ? { prereq_id: prereqId } : {}) }
      });
      console.log('✅ API - getMatchManualCandidatos respuesta:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API - getMatchManualCandidatos error:', error?.response?.status, error?.response?.data);
      throw error;
    }
  }

  // POST /api/match-manual — Marca el documento con el tipo del requisito (forzando si ya
  // tenía otro) para que acredite en TODOS los clientes que lo pidan, con vigencia. Desde
  // 2026-09-23 ya NO crea una fila local: es una designación de tipo, no una asociación por
  // cliente. `nota` se ignora si viene (no hay tabla que la reciba).
  // Si el requisito no tiene tipo, el backend ya no responde 422 (2026-10-13): asocia el
  // documento directo en match_manual, solo para ese cliente.
  async crearMatchManual(data: { cliente_id: number; rut: string; documento_id: number | string; prereq_id: number | string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/match-manual', data);
    return response.data;
  }

  // DELETE /api/match-manual/:id — Eliminar por ID
  async eliminarMatchManualById(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/match-manual/${id}`);
    return response.data;
  }

  // DELETE /api/match-manual?cliente_id=X&rut=Y&prereq_id=Z — Eliminar por claves
  async eliminarMatchManual(params: { cliente_id: number; rut: string; prereq_id: number | string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete('/match-manual', { params });
    return response.data;
  }

  // POST /api/match-manual/rechazar — Deja el prerrequisito en blanco de forma persistente.
  // Necesario porque borrar el match manual no basta: un match automático (derivado del
  // tipo del documento) se vuelve a calcular en el siguiente fetch.
  async rechazarMatch(data: { cliente_id: number; rut: string; prereq_id: number | string; nota?: string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/match-manual/rechazar', data);
    return response.data;
  }

  // DELETE /api/match-manual/rechazar?cliente_id=X&rut=Y&prereq_id=Z — Levanta el rechazo.
  async quitarRechazoMatch(params: { cliente_id: number; rut: string; prereq_id: number | string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete('/match-manual/rechazar', { params });
    return response.data;
  }

  // GET /api/requisitos/clientes/:clienteId/faltantes/:rut
  async getPrerequisitosDetailByRut(clienteId: number, rut: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<any>> {
    const encodedRut = encodeURIComponent(String(rut));
    console.log(`🔍 [API] Obteniendo requisitos para cliente ${clienteId}, RUT ${rut}`);
    const label = `GET /requisitos/clientes/${clienteId}/faltantes/${encodedRut}`;
    cloudRunLog('Request start', { url: label });
    cloudRunTimeStart(label);
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(
        `/requisitos/clientes/${clienteId}/faltantes/${encodedRut}`,
        `/prerrequisitos/clientes/${clienteId}/faltantes/${encodedRut}`,
        { params }
      );
      cloudRunTimeEnd(label);
      console.log(`✅ [API] Respuesta requisitos recibida:`, response.data);
      cloudRunSummarize(response);
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        // Cliente without requisitos configured — return empty gracefully
        cloudRunTimeEnd(label);
        return { success: true, data: [] };
      }
      console.error(`❌ [API] Error al obtener prerrequisitos:`, {
        clienteId,
        rut,
        status,
        message: error.response?.data?.message || error.message,
        url: error.config?.url
      });
      cloudRunLog('Request error', { url: label, status, message: error.response?.data?.message || error.message });
      throw error;
    }
  }

  // GET /api/prerrequisitos/catalogo-areas — catálogo liviano tipo_documento → área
  // (RRHH | Prevención | N/A), sin acotar por cliente. Pensado para vistas que listan
  // documentos de personas sin contexto de cliente (ej. Estado de Documentación),
  // donde recorrer todos los clientes sería excesivo. 1 sola query en el backend.
  async getCatalogoAreasPrerrequisitos(): Promise<ApiResponse<Array<{ tipo_documento: string; area: PrereqArea }>>> {
    const response: AxiosResponse<any> = await this.api.get('/prerrequisitos/catalogo-areas');
    return response.data;
  }

  // GET /api/requisitos/globales
  async getGlobalPrerrequisitos(): Promise<ApiResponse<any[]>> {
    const resp: AxiosResponse<ApiResponse<any[]>> = await this.getWithLegacy('/prerrequisitos/globales', '/requisitos/globales');
    return resp.data;
  }

  // GET /api/requisitos
  async getPrerrequisitos(): Promise<ApiResponse<any[]>> {
    const resp: AxiosResponse<ApiResponse<any[]>> = await this.getWithLegacy('/prerrequisitos', '/requisitos');
    return resp.data;
  }

  // POST /api/prerrequisitos
  async crearPrerrequisito(data: any): Promise<ApiResponse<any>> {
    const payload = { ...data };
    // Si cliente_id es null, el backend puede rechazarlo. Lo eliminamos si es el caso.
    if (payload.cliente_id === null) {
      delete payload.cliente_id;
    }
    const resp: AxiosResponse<ApiResponse<any>> = await this.postWithLegacy('/prerrequisitos', '/requisitos', payload);
    return resp.data;
  }

  // PUT /api/requisitos/:id
  async actualizarPrerrequisito(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/requisitos/${id}`, data);
    return response.data;
  }

  // DELETE /api/requisitos/:id
  async eliminarPrerrequisito(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/requisitos/${id}`);
    return response.data;
  }

  // ==================== MÉTODOS PARA MIGRACIÓN ====================
  
  // GET /api/migration
  async getMigrationStatus(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/migration');
    return response.data;
  }

  // POST /api/migration
  async runMigration(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/migration');
    return response.data;
  }

  // ==================== MÉTODOS PARA BELRAY ====================
  
  // GET /api/belray - Listar todas las empresas Belray
  async getBelray(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    const query: string[] = [];
    if (params?.page !== undefined) query.push(`page=${encodeURIComponent(String(params.page))}`);
    if (params?.limit !== undefined) query.push(`limit=${encodeURIComponent(String(params.limit))}`);
    if (params?.search) query.push(`search=${encodeURIComponent(params.search)}`);
    const url = `/belray${query.length ? `?${query.join('&')}` : ''}`;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(url);
    return response.data;
  }

  // GET /api/belray/:id - Obtener una empresa Belray por ID
  async getBelrayById(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/belray/${id}`);
    return response.data;
  }

  // POST /api/belray - Crear nueva empresa Belray
  async crearBelray(data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/belray', data);
    const result = response.data;
    
    // Validación estricta: el backend DEBE devolver un id
    if (!result.data || !result.data.id) {
      console.error('❌ POST /api/belray no devolvió id en la respuesta:', result);
      throw new Error('El servidor no devolvió el id del registro creado. No se puede continuar.');
    }
    
    return result;
  }

  // PUT /api/belray/:id - Actualizar empresa Belray
  async updateBelray(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/belray/${id}`, data);
    return response.data;
  }

  // DELETE /api/belray/:id - Eliminar empresa Belray
  async deleteBelray(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/belray/${id}`);
    return response.data;
  }

  // POST /api/belray/:id/editar-seguro - Editar empresa usando endpoint seguro (evita columnas no permitidas)
  async editarBelraySeguro(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/belray/${id}/editar-seguro`, data);
    return response.data;
  }

  // GET /api/belray/:id/acreditacion - Obtener acreditación de una empresa Belray
  async getBelrayAcreditacion(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/belray/${id}/acreditacion`);
    return response.data;
  }

  // POST /api/belray/:id/acreditacion - Crear o actualizar acreditación
  async saveBelrayAcreditacion(id: number, data: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/belray/${id}/acreditacion`, data);
    return response.data;
  }

  // DELETE /api/belray/:id/acreditacion - Eliminar acreditación
  async deleteBelrayAcreditacion(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/belray/${id}/acreditacion`);
    return response.data;
  }

  // POST /api/belray/:id/documentos - Subir documento para empresa Belray
  async uploadBelrayDocument(id: number, formData: FormData): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/belray/${id}/documentos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // GET /api/belray/:id/documentos - Listar documentos de una empresa Belray
  async getBelrayDocuments(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/belray/${id}/documentos`);
    return response.data;
  }

  // DELETE /api/belray/:id/documentos/:filename - Eliminar documento de empresa Belray
  async deleteBelrayDocument(id: number, filename: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(
      `/belray/${id}/documentos/${encodeURIComponent(filename)}`
    );
    return response.data;
  }

  // GET /api/belray/:id/documentos/:filename - Descargar documento de empresa Belray
  async downloadBelrayDocument(id: number, filename: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(
      `/belray/${id}/documentos/${encodeURIComponent(filename)}`,
      {
        responseType: 'blob',
        timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
      }
    );
    return response.data;
  }

  // ==================== DOCUMENTOS DE EMPRESA ====================

  // GET /api/empresa-documentos?empresa_id=X - Listar documentos de una empresa
  async getEmpresaDocumentos(empresaId: number): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get(`/empresa-documentos?empresa_id=${empresaId}`);
    return response.data;
  }

  // GET /api/empresa-documentos/:id - Obtener documento específico
  async getEmpresaDocumentoById(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/empresa-documentos/${id}`);
    return response.data;
  }

  // POST /api/empresa-documentos/subir - Subir documento
  async subirEmpresaDocumento(formData: FormData): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/empresa-documentos/subir', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Sube a Drive un archivo de más de FILE_CONFIG.MAX_SIZE en trozos (backend:
  // routes/subida-grande.js), porque Cloud Run corta cualquier request de más de 32 MiB.
  // Devuelve el token que la ruta de registro recibe en `archivo_subido` en vez de `file`.
  // Un trozo que falla se reintenta preguntando antes cuántos bytes ya tiene Drive.
  async subirArchivoPorTrozos(file: File, onProgress?: (porcentaje: number) => void): Promise<string> {
    const ini = await this.api.post('/subida-grande/iniciar', {
      nombre: file.name,
      mime_type: file.type,
      tamano: file.size,
    });
    const subida: string = ini.data.subida;
    const trozo: number = ini.data.trozo;
    const qs = `subida=${encodeURIComponent(subida)}`;

    let inicio = 0;
    let fallosSeguidos = 0;
    while (true) {
      try {
        const cuerpo = file.slice(inicio, Math.min(inicio + trozo, file.size));
        const r = await this.api.put(`/subida-grande/trozo?${qs}&inicio=${inicio}`, cuerpo, {
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
        });
        fallosSeguidos = 0;
        if (r.data.completo) {
          onProgress?.(100);
          return r.data.archivo;
        }
        inicio = r.data.recibido;
      } catch (err: any) {
        // Un 400 (tipo, tamaño, token vencido) no se arregla reintentando.
        if (err?.response?.status === 400 || ++fallosSeguidos > 3) throw err;
        await new Promise(res => setTimeout(res, 1000 * fallosSeguidos));
        const estado = await this.api.get(`/subida-grande/estado?${qs}`);
        if (estado.data.completo) {
          onProgress?.(100);
          return estado.data.archivo;
        }
        inicio = estado.data.recibido;
      }
      onProgress?.(Math.round((inicio / file.size) * 100));
    }
  }

  // PUT /api/empresa-documentos/:id - Actualizar metadata de documento
  async updateEmpresaDocumento(id: number, data: any): Promise<any> {
    let payload = data;
    let headers: Record<string, string> | undefined;
    if (data?.archivo instanceof File) {
      const formData = new FormData();
      formData.append('file', data.archivo);
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'archivo' || value === undefined || value === null) return;
        formData.append(key, String(value));
      });
      payload = formData;
      headers = { 'Content-Type': 'multipart/form-data' };
    }
    const response: AxiosResponse<any> = await this.api.put(`/empresa-documentos/${id}`, payload, { headers });
    return response.data;
  }

  // DELETE /api/empresa-documentos/:id - Eliminar documento
  async deleteEmpresaDocumento(id: number, eliminarFisicamente: boolean = false): Promise<any> {
    const url = eliminarFisicamente 
      ? `/empresa-documentos/${id}?eliminar_fisicamente=true`
      : `/empresa-documentos/${id}`;
    const response: AxiosResponse<any> = await this.api.delete(url);
    return response.data;
  }

  // ==================== SOLICITUDES DE DOCUMENTOS DE EMPRESA ====================
  // Staging sobre empresa-documentos: 'peticion' pide uno que falta, 'carga' sube uno que
  // queda pendiente de aprobación. Ver zc_backend/routes/empresa-documento-solicitudes.js.

  // POST /api/empresa-documento-solicitudes - Crear solicitud (multipart si es 'carga')
  async crearSolicitudDocumentoEmpresa(formData: FormData): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/empresa-documento-solicitudes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // GET /api/empresa-documento-solicitudes - Bandeja (el backend filtra a lo propio si
  // la cuenta no tiene empresa_documents.review)
  async getSolicitudesDocumentoEmpresa(params: {
    estado?: string;
    empresa_id?: number;
    tipo_solicitud?: string;
    solo_mias?: boolean;
  } = {}): Promise<any> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.append(key, String(value));
    });
    const sufijo = qs.toString() ? `?${qs.toString()}` : '';
    const response: AxiosResponse<any> = await this.api.get(`/empresa-documento-solicitudes${sufijo}`);
    return response.data;
  }

  // POST /api/empresa-documento-solicitudes/:id/aprobar
  async aprobarSolicitudDocumentoEmpresa(id: number, observaciones?: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      `/empresa-documento-solicitudes/${id}/aprobar`,
      { observaciones }
    );
    return response.data;
  }

  // GET /api/empresa-documento-solicitudes/:id/archivo - archivo de una 'carga' (inline),
  // para verlo antes de aprobar. Lo abre el revisor o quien la envió; el F30 da 403.
  async visualizarArchivoSolicitudEmpresa(id: number): Promise<{ blob: Blob }> {
    const response = await this.api.get(`/empresa-documento-solicitudes/${id}/archivo`, {
      responseType: 'blob',
      timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
    });
    return { blob: response.data };
  }

  // POST /api/empresa-documento-solicitudes/:id/rechazar - motivo obligatorio
  async rechazarSolicitudDocumentoEmpresa(id: number, motivo: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      `/empresa-documento-solicitudes/${id}/rechazar`,
      { motivo }
    );
    return response.data;
  }

  // GET /api/personal-disponible/contadores - total, activos y distribución por estado en una
  // sola consulta agregada. Evita descargar la lista completa de personal solo para contarla.
  async getPersonalContadores(): Promise<ApiResponse<{
    total: number;
    activos: number;
    por_estado: { id: number; nombre: string; cantidad: number }[];
  }>> {
    const response = await this.api.get('/personal-disponible/contadores');
    return response.data;
  }

  // GET /api/documentos/contadores - los tres totales de la tarjeta "Documentación" del
  // dashboard, calculados en el servidor. Disponible para cualquier usuario autenticado, así
  // que ninguna cuenta necesita descargar el listado completo de documentos para contarlos.
  async getDocumentosContadores(diasAlerta = 30): Promise<ApiResponse<{
    total: number;
    vencidos: number;
    vencidos_ya_reemplazados: number;
    por_vencer: number;
    dias_alerta: number;
  }>> {
    const response = await this.api.get('/documentos/contadores', { params: { dias_alerta: diasAlerta } });
    return response.data;
  }

  // GET /api/empresa-documentos/estadisticas/empresa/:empresaId - Estadísticas de documentos
  async getEstadisticasDocumentos(empresaId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/empresa-documentos/estadisticas/empresa/${empresaId}`);
    return response.data;
  }

  // GET /api/empresa-documentos/visualizar/:id - Ver documento (inline, vía cuenta de servicio)
  // onProgress recibe los bytes recibidos y el total. El backend envía por partes (sin
  // Content-Length) y manda el total en X-Tamano-Archivo; si no llega, total es null.
  async visualizarEmpresaDocumento(
    id: number,
    onProgress?: (p: { cargados: number; total: number | null }) => void,
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; filename: string }> {
    let total: number | null = null;
    const response = await this.api.get(`/empresa-documentos/visualizar/${id}`, {
      responseType: 'blob',
      timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
      signal,
      headers: { 'Accept': 'application/octet-stream, application/pdf, */*' },
      onDownloadProgress: onProgress && ((e) => {
        if (!total) {
          // getAllResponseHeaders y no getResponseHeader: este último llena la consola de
          // "Refused to get unsafe header" si el backend no expone la cabecera.
          const xhr = (e.event as any)?.target as XMLHttpRequest | undefined;
          const m = xhr?.getAllResponseHeaders?.().match(/^x-tamano-archivo:\s*(\d+)/im);
          total = e.total || (m ? Number(m[1]) : null);
        }
        onProgress({ cargados: e.loaded, total });
      }),
    });
    const contentDisposition = response.headers['content-disposition'];
    let filename = '';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) {
        try { filename = decodeURIComponent(match[1].replace(/['"]/g, '')); } catch { filename = match[1].replace(/['"]/g, ''); }
      }
    }
    return { blob: response.data, filename };
  }

  // ==================== AUDITORÍAS DE CUMPLIMIENTO ====================
  // NO confundir con el log de uso de plataforma (sistema.auditoria_log,
  // /api/auditoria, ConfiguracionAuditoriaPage). Esto es un paquete de requisitos
  // empresa+persona que un mandante debe cumplir (ver docs backend Fases 0-3).

  // GET /api/auditorias-cumplimiento - Listar auditorías
  async getAuditoriasCumplimiento(params?: { mandante_belray_id?: number; activa?: boolean }): Promise<ApiResponse<any>> {
    const query: string[] = [];
    if (params?.mandante_belray_id !== undefined) query.push(`mandante_belray_id=${params.mandante_belray_id}`);
    if (params?.activa !== undefined) query.push(`activa=${params.activa}`);
    const url = `/auditorias-cumplimiento${query.length ? `?${query.join('&')}` : ''}`;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(url);
    return response.data;
  }

  // GET /api/auditorias-cumplimiento/:id - Obtener auditoría
  async getAuditoriaCumplimiento(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditorias-cumplimiento/${id}`);
    return response.data;
  }

  // POST /api/auditorias-cumplimiento - Crear auditoría
  async crearAuditoriaCumplimiento(data: { nombre: string; mandante_belray_id: number; cliente_base_id?: number; observaciones?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auditorias-cumplimiento', data);
    return response.data;
  }

  // PUT /api/auditorias-cumplimiento/:id - Actualizar auditoría
  async actualizarAuditoriaCumplimiento(id: number, data: { nombre?: string; cliente_base_id?: number; activa?: boolean; observaciones?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/auditorias-cumplimiento/${id}`, data);
    return response.data;
  }

  // DELETE /api/auditorias-cumplimiento/:id - Desactivar (o ?permanent=true para borrar)
  async eliminarAuditoriaCumplimiento(id: number, permanent = false): Promise<ApiResponse<any>> {
    const url = `/auditorias-cumplimiento/${id}${permanent ? '?permanent=true' : ''}`;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(url);
    return response.data;
  }

  // GET /api/auditorias-cumplimiento/:id/veredicto - PASA/NO_PASA + detalle
  async getVeredictoAuditoria(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditorias-cumplimiento/${id}/veredicto`);
    return response.data;
  }

  // GET /api/auditorias-cumplimiento/:id/reporte - veredicto + % y ranking de faltantes
  async getReporteAuditoria(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditorias-cumplimiento/${id}/reporte`);
    return response.data;
  }

  // GET /api/auditorias-cumplimiento/persona/:rut - variante desde la persona: en qué
  // auditorías está en alcance y si cumple los requisitos de nivel persona de cada una
  async getAuditoriasPersona(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/auditorias-cumplimiento/persona/${encodeURIComponent(rut)}`);
    return response.data;
  }

  // ---- Match manual (auditoría): asociar a mano un documento a un requisito cuando
  // el match automático no lo reconoce. Espejo de match-manual (Acreditación) pero
  // sobre mantenimiento.auditoria_requisitos / auditoria_match_manual.

  async getAuditoriaMatchManual(auditoriaId: number, rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/auditorias-cumplimiento/match-manual?auditoria_id=${auditoriaId}&rut=${encodeURIComponent(rut)}`
    );
    return response.data;
  }

  async getAuditoriaMatchManualCandidatos(auditoriaId: number, rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/auditorias-cumplimiento/match-manual/candidatos?auditoria_id=${auditoriaId}&rut=${encodeURIComponent(rut)}`
    );
    return response.data;
  }

  async crearAuditoriaMatchManual(data: { auditoria_id: number; requisito_id: number; rut: string; documento_id: number; nota?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auditorias-cumplimiento/match-manual', data);
    return response.data;
  }

  async eliminarAuditoriaMatchManual(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/auditorias-cumplimiento/match-manual/${id}`);
    return response.data;
  }

  // POST /api/auditorias-cumplimiento/:id/procesar - Proceso formal de aprobación
  // (solo funciona si el veredicto actual es PASA; espejo de acreditar empresa)
  async procesarAuditoria(id: number, data?: { observaciones?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/auditorias-cumplimiento/${id}/procesar`, data || {});
    return response.data;
  }

  // ==================== REQUISITOS DE EMPRESA ====================

  // GET /api/empresa-requisitos - Listar requisitos
  async getEmpresaPrerrequisitos(params?: { empresa_id?: number; includeGlobal?: boolean }): Promise<ApiResponse<any>> {
    const query: string[] = [];
    if (params?.empresa_id !== undefined) query.push(`empresa_id=${params.empresa_id}`);
    if (params?.includeGlobal !== undefined) query.push(`includeGlobal=${params.includeGlobal}`);
    const url = `/empresa-requisitos${query.length ? `?${query.join('&')}` : ''}`;
    const legacyUrl = `/empresa-prerrequisitos${query.length ? `?${query.join('&')}` : ''}`;
    const resp: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(url, legacyUrl);
    return resp.data;
  }

  // GET /api/empresa-requisitos/:id - Obtener requisito específico
  async getEmpresaPrerequisitoById(id: number): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(`/empresa-requisitos/${id}`, `/empresa-prerrequisitos/${id}`);
    return resp.data;
  }

  // POST /api/empresa-requisitos - Crear requisito
  async crearEmpresaPrerrequisito(data: any): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.postWithLegacy('/empresa-requisitos', '/empresa-prerrequisitos', data);
    return resp.data;
  }

  // PUT /api/empresa-requisitos/:id - Actualizar requisito
  async updateEmpresaPrerrequisito(id: number, data: any): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.putWithLegacy(`/empresa-requisitos/${id}`, `/empresa-prerrequisitos/${id}`, data);
    return resp.data;
  }

  // DELETE /api/empresa-requisitos/:id - Eliminar requisito
  async deleteEmpresaPrerrequisito(id: number): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.deleteWithLegacy(`/empresa-requisitos/${id}`, `/empresa-prerrequisitos/${id}`);
    return resp.data;
  }

  // GET /api/empresa-requisitos/validar/:empresaId - Validar requisitos de empresa
  async validarPrerrequisitosEmpresa(empresaId: number): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.getWithLegacy(`/empresa-requisitos/validar/${empresaId}`, `/empresa-prerrequisitos/validar/${empresaId}`);
    return resp.data;
  }

  // GET /api/acreditacion/empresas/:empresaId/context - Contexto compuesto para /acreditacion
  async getEmpresaAcreditacionContext(empresaId: number, params?: { includeGlobal?: boolean; fresh?: boolean }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/acreditacion/empresas/${empresaId}/context`, {
      params: {
        includeGlobal: params?.includeGlobal ?? true,
        fresh: params?.fresh ?? false,
      }
    });
    return response.data;
  }

  // POST /api/empresa-requisitos/acreditar/:empresaId - Acreditar empresa
  async acreditarEmpresa(empresaId: number, data: { acreditada_por: string; observaciones?: string }): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.postWithLegacy(`/empresa-requisitos/acreditar/${empresaId}`, `/empresa-prerrequisitos/acreditar/${empresaId}`, data);
    return resp.data;
  }

  // POST /api/empresa-requisitos/desacreditar/:empresaId - Desacreditar empresa
  async desacreditarEmpresa(empresaId: number, data: { estado: string; motivo: string; usuario: string }): Promise<ApiResponse<any>> {
    const resp: AxiosResponse<ApiResponse<any>> = await this.postWithLegacy(`/empresa-requisitos/desacreditar/${empresaId}`, `/empresa-prerrequisitos/desacreditar/${empresaId}`, data);
    return resp.data;
  }

  // ==================== MÉTODOS DE BÚSQUEDA GLOBAL ====================

  // GET /api/search/personal?q=query (búsqueda global de personal)
  async searchPersonalGlobal(query: string): Promise<Personal[]> {
    try {
      const response: AxiosResponse<ApiResponse<Personal[]>> = await this.api.get(`/search/personal?q=${encodeURIComponent(query)}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error buscando personal:', error);
      return [];
    }
  }

  // GET /api/search/clientes?q=query
  async searchClientesGlobal(query: string): Promise<Cliente[]> {
    try {
      const response: AxiosResponse<ApiResponse<Cliente[]>> = await this.api.get(`/search/clientes?q=${encodeURIComponent(query)}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error buscando clientes:', error);
      return [];
    }
  }

  // GET /api/search/nodos?q=query
  async searchNodosGlobal(query: string): Promise<Nodo[]> {
    try {
      const response: AxiosResponse<ApiResponse<Nodo[]>> = await this.api.get(`/search/nodos?q=${encodeURIComponent(query)}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error buscando nodos:', error);
      return [];
    }
  }

  // GET /api/search/carteras?q=query
  async searchCarterasGlobal(query: string): Promise<Cartera[]> {
    try {
      const response: AxiosResponse<ApiResponse<Cartera[]>> = await this.api.get(`/search/carteras?q=${encodeURIComponent(query)}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error buscando carteras:', error);
      return [];
    }
  }

  // GET /api/search/global?q=query (búsqueda unificada)
  async searchGlobal(query: string): Promise<{
    personal: Personal[];
    clientes: Cliente[];
    nodos: Nodo[];
    carteras: Cartera[];
  }> {
    try {
      const response: AxiosResponse<ApiResponse<{
        personal: Personal[];
        clientes: Cliente[];
        nodos: Nodo[];
        carteras: Cartera[];
      }>> = await this.api.get(`/search/global?q=${encodeURIComponent(query)}`);
      return response.data.data || {
        personal: [],
        clientes: [],
        nodos: [],
        carteras: []
      };
    } catch (error) {
      console.error('Error en búsqueda global:', error);
      return {
        personal: [],
        clientes: [],
        nodos: [],
        carteras: []
      };
    }
  }

  // ─── Acreditación de Personal por Cliente ────────────────────────────────

  async getAcreditacionPersonal(
    rut: string,
    params?: { search?: string; acreditado?: boolean; cartera_id?: number; page?: number }
  ): Promise<ApiResponse<any>> {
    const query: Record<string, any> = {};
    if (params) {
      if (params.search !== undefined) query.search = params.search;
      if (params.acreditado !== undefined) query.acreditado = params.acreditado;
      if (params.cartera_id !== undefined) query.cartera_id = params.cartera_id;
      if (params.page !== undefined) query.page = params.page;
    }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/acreditacion-clientes/${encodeURIComponent(rut)}`,
      { params: query }
    );
    return response.data;
  }

  async getResumenAcreditacion(rut: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/acreditacion-clientes/${encodeURIComponent(rut)}/resumen`
    );
    return response.data;
  }

  // ─── Batch endpoints (BFF) ────────────────────────────────────────────────

  /** Devuelve { [rut]: { acreditados, no_acreditados, total } } para un array de RUTs. */
  async getResumenAcreditacionBatch(ruts: string[]): Promise<{ success: boolean; data: Record<string, { acreditados: number; no_acreditados: number; total: number }> }> {
    const response: AxiosResponse<any> = await this.api.post(
      '/acreditacion-clientes/resumen-batch',
      { ruts }
    );
    return response.data;
  }

  /** Devuelve { [rut]: [{ cliente_id, nombre_cliente, acreditado, fecha_acreditacion }] } para un array de RUTs. */
  async getDetalleAcreditacionBatch(ruts: string[], soloAcreditados = true): Promise<{ success: boolean; data: Record<string, Array<{ cliente_id: string; nombre_cliente: string; acreditado: boolean; fecha_acreditacion: string | null }>> }> {
    const response: AxiosResponse<any> = await this.api.post(
      '/acreditacion-clientes/detalle-batch',
      { ruts, solo_acreditados: soloAcreditados }
    );
    return response.data;
  }

  /**
   * GET /api/acreditacion-clientes/cobertura-masiva — cobertura documental de TODO el
   * personal de un cliente, leída desde la tabla pre-calculada zc_temp.cobertura_resumen
   * (refrescada cada 30 min). Responde en <20ms, sin matching en vivo.
   * Devuelve { [rut]: { covered, total } } ya armado para consumo directo.
   */
  async getCoberturaMasivaCliente(clienteId: number): Promise<Record<string, { covered: number; total: number }>> {
    const limit = 500;
    let page = 1;
    let pages = 1;
    const out: Record<string, { covered: number; total: number }> = {};
    do {
      const response: AxiosResponse<any> = await this.api.get(
        '/acreditacion-clientes/cobertura-masiva',
        { params: { cliente_id: clienteId, page, limit } }
      );
      const body = response.data || {};
      const rows: any[] = Array.isArray(body.data) ? body.data : [];
      rows.forEach(r => {
        out[String(r.rut_persona)] = { covered: Number(r.cubiertos) || 0, total: Number(r.total_reqs) || 0 };
      });
      pages = Number(body.pagination?.pages) || 1;
      page += 1;
    } while (page <= pages);
    return out;
  }


  /**
   * POST /api/requisitos/clientes/:id/cobertura-masiva — cobertura calculada en vivo
   * usando matchForCliente (misma lógica que el modal de detalle), sin reconciliación extra.
   * Devuelve { [rut]: { covered, total } } preciso para la matriz de cobertura.
   */
  async getCoberturaMasivaLive(clienteId: number, ruts: string[]): Promise<Record<string, { covered: number; total: number }>> {
    if (ruts.length === 0) return {};
    const response: AxiosResponse<any> = await this.api.post(
      `/requisitos/clientes/${encodeURIComponent(String(clienteId))}/cobertura-masiva`,
      { ruts }
    );
    const raw: Record<string, { prereq_covered_count: number; prereq_total: number }> = response.data?.data ?? {};
    const out: Record<string, { covered: number; total: number }> = {};
    Object.entries(raw).forEach(([rut, r]) => {
      out[rut] = { covered: Number(r.prereq_covered_count) || 0, total: Number(r.prereq_total) || 0 };
    });
    return out;
  }

  /**
   * GET /api/acreditacion-clientes/cobertura-masiva-multi — cobertura de VARIOS clientes
   * en una sola llamada de red (en vez de una por columna). Pensado para la carga inicial
   * de la matriz de cobertura. Devuelve { [clienteId]: { [rut]: { covered, total } } }.
   */
  async getCoberturaMasivaMulti(clienteIds: number[]): Promise<Record<string, Record<string, { covered: number; total: number }>>> {
    if (clienteIds.length === 0) return {};
    const response: AxiosResponse<any> = await this.api.get(
      '/acreditacion-clientes/cobertura-masiva-multi',
      { params: { cliente_ids: clienteIds.join(',') } }
    );
    const raw: Record<string, any[]> = response.data?.data || {};
    const out: Record<string, Record<string, { covered: number; total: number }>> = {};
    Object.entries(raw).forEach(([cid, rows]) => {
      const m: Record<string, { covered: number; total: number }> = {};
      (rows || []).forEach((r: any) => {
        m[String(r.rut_persona)] = { covered: Number(r.cubiertos) || 0, total: Number(r.total_reqs) || 0 };
      });
      out[cid] = m;
    });
    return out;
  }

  /**
   * GET /api/acreditacion-clientes/cobertura-matriz — ÚLTIMO cálculo de cobertura guardado,
   * el mismo para todos los usuarios, en una sola query. Formato compacto del backend:
   * { [clienteId]: { [rutNormalizado]: [cubiertos, total] } }.
   */
  async getCoberturaMatriz(clienteIds: number[]): Promise<{
    data: Record<string, Record<string, { covered: number; total: number }>>;
    meta: CoberturaMatrizMeta | null;
  }> {
    const response: AxiosResponse<any> = await this.api.get(
      '/acreditacion-clientes/cobertura-matriz',
      { params: clienteIds.length > 0 ? { cliente_ids: clienteIds.join(',') } : {} }
    );
    const raw: Record<string, Record<string, [number, number]>> = response.data?.data || {};
    const data: Record<string, Record<string, { covered: number; total: number }>> = {};
    Object.entries(raw).forEach(([cid, porRut]) => {
      const m: Record<string, { covered: number; total: number }> = {};
      Object.entries(porRut || {}).forEach(([rut, par]) => {
        m[rut] = { covered: Number(par?.[0]) || 0, total: Number(par?.[1]) || 0 };
      });
      data[cid] = m;
    });
    return { data, meta: response.data?.meta ?? null };
  }

  /**
   * POST /api/acreditacion-clientes/cobertura-persona/:rut/recalcular — recalcula YA la
   * cobertura de UNA persona en todos los clientes (~0,3 s) y la devuelve como
   * { [cliente_id]: { covered, total } }. Para refrescar solo sus filas tras cambiar sus
   * documentos o match manuales, sin recargar la página.
   */
  async recalcularCoberturaPersona(rut: string): Promise<Record<string, { covered: number; total: number }>> {
    const response: AxiosResponse<any> = await this.api.post(
      `/acreditacion-clientes/cobertura-persona/${encodeURIComponent(rut)}/recalcular`,
      {}
    );
    return response.data?.data || {};
  }

  /**
   * POST /api/acreditacion-clientes/cobertura-matriz/recalcular — rehace TODA la cobertura
   * en un solo disparo (~5 s en el servidor). 409 si otro usuario ya está recalculando.
   */
  async recalcularCoberturaMatriz(): Promise<{ success: boolean; meta: CoberturaMatrizMeta | null; message?: string }> {
    const response: AxiosResponse<any> = await this.api.post(
      '/acreditacion-clientes/cobertura-matriz/recalcular',
      {},
      { timeout: 120000 }
    );
    return { success: !!response.data?.success, meta: response.data?.meta ?? null, message: response.data?.message };
  }

  /** Devuelve { cliente_ids: string[], nodo_ids: string[] } de toda la planificación histórica. */
  async getClientesActivosPlanificacion(carteraIds?: (string | number)[]): Promise<{ success: boolean; data: { cliente_ids: string[]; nodo_ids: string[] } }> {
    const params: Record<string, string> = {};
    if (carteraIds && carteraIds.length > 0) params.cartera_ids = carteraIds.join(',');
    const response: AxiosResponse<any> = await this.api.get(
      '/programacion-semanal/clientes-activos',
      { params }
    );
    return response.data;
  }

  async acreditarEnClientes(
    rut: string,
    body: { cliente_ids: number[]; acreditado_por?: string; observaciones?: string }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/acreditacion-clientes/${encodeURIComponent(rut)}/clientes`,
      body
    );
    return response.data;
  }

  async acreditarEnCliente(
    rut: string,
    clienteId: number,
    body?: { acreditado_por?: string; observaciones?: string }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/acreditacion-clientes/${encodeURIComponent(rut)}/cliente/${clienteId}`,
      body ?? {}
    );
    return response.data;
  }

  async revocarClientes(
    rut: string,
    body: { cliente_ids: number[] }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(
      `/acreditacion-clientes/${encodeURIComponent(rut)}/clientes`,
      { data: body }
    );
    return response.data;
  }

  async revocarCliente(rut: string, clienteId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(
      `/acreditacion-clientes/${encodeURIComponent(rut)}/cliente/${clienteId}`
    );
    return response.data;
  }

  // ==================== PERSONAL BASE ====================

  /** GET /api/personal-base/todos — todos los clientes agrupados en una sola llamada */
  async getPersonalBaseTodos(): Promise<{ success: boolean; count: number; data: Record<string, { cliente_id: number; cliente_nombre: string; base: any[]; adicional: any[] }> }> {
    const response: AxiosResponse<any> = await this.api.get('/personal-base/todos');
    return response.data;
  }

  /** GET /api/personal-base/cliente/:id — detalle con requisitos */
  async getPersonalBaseCliente(clienteId: number): Promise<{ base: any[]; adicional: any[]; requisitos: any[] }> {
    const response: AxiosResponse<any> = await this.api.get(`/personal-base/cliente/${clienteId}`);
    return response.data;
  }

  /** POST /api/personal-base */
  async addPersonaBase(payload: {
    cliente_id: number;
    nombre: string;
    tipo: 'base' | 'adicional';
    cargo?: string;
    rut?: string;
    notas?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/personal-base', payload);
    return response.data;
  }

  /** PUT /api/personal-base/:id */
  async updatePersonaBase(
    id: number,
    cambios: Partial<{ nombre: string; cargo: string; tipo: 'base' | 'adicional'; rut: string; notas: string; activo: boolean }>
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/personal-base/${id}`, cambios);
    return response.data;
  }

  /** DELETE /api/personal-base/:id */
  async deletePersonaBase(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/personal-base/${id}`);
    return response.data;
  }

  /** POST /api/personal-base/requisito */
  async addRequisitoPersonal(payload: { cliente_id: number; descripcion: string; orden?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/personal-base/requisito', payload);
    return response.data;
  }

  /** DELETE /api/personal-base/requisito/:id */
  async deleteRequisitoPersonal(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/personal-base/requisito/${id}`);
    return response.data;
  }

  // ==================== CACHE ====================

  /** POST /api/cache/refresh — fuerza refresh de materialized views */
  async refreshCacheBackend(): Promise<{ success: boolean; results?: any[]; lastRefreshedAt?: string; totalMs?: number }> {
    const response: AxiosResponse<any> = await this.api.post('/cache/refresh');
    return response.data;
  }

  /** GET /api/cache/status */
  async getCacheStatus(): Promise<{ success: boolean; data: { lastRefreshedAt: string; refreshInProgress: boolean; nextRefreshInMs: number; intervalMs: number } }> {
    const response: AxiosResponse<any> = await this.api.get('/cache/status');
    return response.data;
  }

  // ==================== PRE-SISI DE DOCUMENTACIÓN ====================
  // Buzón de staging: la documentación se sube acá, queda 'pendiente', y solo al
  // aprobarla el backend la publica en `documentos` (recién ahí cuenta para cobertura).
  // Contrato completo: docs/BACKEND_PRE_INGRESO_DOCUMENTOS.md
  //
  // Estos endpoints TODAVÍA NO están desplegados. Los GET tratan el 404 como lista
  // vacía para que la página no se caiga mientras tanto; los POST sí propagan el error
  // (una subida que falla en silencio sería peor que un mensaje de error).

  /** POST /api/pre-sisi/documentos — sube un archivo al buzón (queda 'pendiente') */
  async subirPreSisiDocumento(formData: FormData, onProgress?: (percent: number) => void): Promise<ApiResponse<any>> {
    const uploadApi = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
      withCredentials: false,
    });
    const token = typeof window !== 'undefined' ? getTokenIfValid() : null;
    if (!token) {
      clearSession();
      try { window.location.href = getLoginPath(); } catch (e) {}
      throw new Error('Session expired');
    }
    const response: AxiosResponse<ApiResponse<any>> = await uploadApi.post('/pre-sisi/documentos', formData, {
      headers: { Authorization: `Bearer ${token}` },
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
      },
    });
    return response.data;
  }

  /** POST /api/pre-sisi/documentos/:id/editar — solicitud de edición sobre un documento
   *  ya revisado (aprobado o rechazado); crea una fila nueva 'pendiente' enlazada a la
   *  original, mismo criterio de sesión/progreso que subirPreSisiDocumento. */
  async editarPreSisiDocumento(id: number, formData: FormData, onProgress?: (percent: number) => void): Promise<ApiResponse<any>> {
    const uploadApi = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: FILE_CONFIG.UPLOAD_TIMEOUT,
      withCredentials: false,
    });
    const token = typeof window !== 'undefined' ? getTokenIfValid() : null;
    if (!token) {
      clearSession();
      try { window.location.href = getLoginPath(); } catch (e) {}
      throw new Error('Session expired');
    }
    const response: AxiosResponse<ApiResponse<any>> = await uploadApi.post(`/pre-sisi/documentos/${id}/editar`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
      },
    });
    return response.data;
  }

  /** GET /api/pre-sisi/documentos — bandeja, filtrable por estado/rut/cliente */
  async getPreSisiDocumentos(params?: {
    estado?: 'pendiente' | 'aprobado' | 'rechazado';
    rut?: string;
    cliente_id?: number;
    en_papelera?: boolean;
    // Solo lo que subió la cuenta, aunque tenga permiso para ver todo ("Mis solicitudes").
    solo_mias?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<any[]>> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/pre-sisi/documentos', { params });
      const raw: any = response.data;
      const data = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
      return { ...raw, success: true, data };
    } catch (e: any) {
      if (e?.response?.status === 404) return { success: true, data: [] } as ApiResponse<any[]>;
      throw e;
    }
  }

  /** GET /api/pre-sisi/resumen — contadores por estado (badge de la bandeja) */
  async getPreSisiResumen(params?: { solo_mias?: boolean }): Promise<{ pendientes: number; aprobados: number; rechazados: number; papelera: number }> {
    const vacio = { pendientes: 0, aprobados: 0, rechazados: 0, papelera: 0 };
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/pre-sisi/resumen', { params });
      const d: any = response.data?.data ?? response.data ?? {};
      return {
        pendientes: Number(d.pendientes) || 0,
        aprobados: Number(d.aprobados) || 0,
        rechazados: Number(d.rechazados) || 0,
        papelera: Number(d.papelera) || 0,
      };
    } catch (e: any) {
      if (e?.response?.status === 404) return vacio;
      throw e;
    }
  }

  /** GET /api/pre-sisi/documentos/:id/archivo — binario para previsualizar antes de aprobar */
  async getPreSisiArchivo(id: number): Promise<{ blob: Blob; mimeType: string }> {
    const response = await this.api.get(`/pre-sisi/documentos/${id}/archivo`, {
      responseType: 'blob',
      timeout: FILE_CONFIG.DOWNLOAD_TIMEOUT,
      headers: { Accept: 'application/octet-stream, application/pdf, */*' },
    });
    return { blob: response.data, mimeType: response.headers['content-type'] || 'application/octet-stream' };
  }

  /** POST /api/pre-sisi/documentos/:id/aprobar — publica el documento de verdad */
  async aprobarPreSisiDocumento(id: number, data?: { observaciones?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/pre-sisi/documentos/${id}/aprobar`, data || {});
    return response.data;
  }

  /** POST /api/pre-sisi/documentos/:id/rechazar — `motivo` es obligatorio */
  async rechazarPreSisiDocumento(id: number, motivo: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/pre-sisi/documentos/${id}/rechazar`, { motivo });
    return response.data;
  }

  /** POST /api/pre-sisi/documentos/aprobar-lote — cada id se resuelve por separado */
  async aprobarPreSisiLote(ids: number[]): Promise<ApiResponse<{ aprobados: number; fallidos: number; resultados: any[] }>> {
    const response: AxiosResponse<any> = await this.api.post('/pre-sisi/documentos/aprobar-lote', { ids });
    return response.data;
  }

  /** DELETE /api/pre-sisi/documentos/:id — retirar algo que subí y aún no revisan, o eliminar definitivamente desde la papelera */
  async eliminarPreSisiDocumento(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/pre-sisi/documentos/${id}`);
    return response.data;
  }

  /** POST /api/pre-sisi/documentos/:id/papelera — envía a la papelera un documento ya revisado */
  async moverPreSisiDocumentoAPapelera(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/pre-sisi/documentos/${id}/papelera`, {});
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
